/**
 * LinkedIn Inbox - Draft Approval UI
 * WhatsApp-style conversation view with Approve/Edit/Reject actions
 */

import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDebouncedCallback } from 'use-debounce';
import { AnimatePresence, motion, MotionConfig } from 'framer-motion';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
  useResizableDefaultLayout,
} from '@/components/ui/resizable';
import { Layout } from '@/components/layout/Layout';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { CommandCenter } from '@/components/inbox/CommandCenter';
import { GhostTextEditor } from '@/components/inbox/GhostTextEditor';
import { AlternativesPicker, type DraftAlternative } from '@/components/inbox/AlternativesPicker';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import {
  AlertCircle,
  Briefcase,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Edit3,
  Filter,
  Inbox as InboxIcon,
  Keyboard,
  LinkedinIcon,
  MoreHorizontal,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  Trash2,
  User,
  X,
  XCircle,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { sendReply, validateLinkedInMessage } from '@/lib/integrations/heyreach';
import {
  isLinkedInSendContained,
  LINKEDIN_SEND_CONTAINMENT_MESSAGE,
} from '@/lib/integrations/linkedin-send-containment';
import {
  DraftListItem,
  useRegenerateDraft,
  useGenerateDraft,
  useLinkedInDraftFilters,
  type ConstraintValidation,
  type DraftSourceFilter,
} from '@/hooks/useLinkedInDrafts';
import {
  describeGateBlock,
  describeGateBlockWithOverride,
  describeGateOverrideSuccess,
  describeRegenerateResult,
  REPLY_GATE_OVERRIDE_ACTION_ALT_TEXT,
  REPLY_GATE_OVERRIDE_ACTION_LABEL,
} from '@/lib/crm/reply-gate-copy';
import {
  ConversationThread,
  useConversationCampaigns,
  useConversationThreads,
  useThreadById,
  useThreadFilters,
  useThreadCounts,
  type ThreadFilters,
  type ThreadQueryResult,
  type ThreadStatus,
} from '@/hooks/useConversationThreads';
import { useThreadMessages, getLiveDraftWinner } from '@/hooks/useThreadMessages';
import { useConversationHistory } from '@/hooks/useConversationHistory';
import {
  useSoftDeleteThreads,
  useRestoreThreads,
  useBulkResolveThreads,
  useDismissFollowUp,
} from '@/hooks/useThreadActions';
import {
  useDraftCampaignDomain,
  useReplyQueue,
  useReplyQueueCount,
} from '@/hooks/useReplyQueue';
import { ReplyQueuePanel, leadDossierHref } from '@/components/inbox/reply-queue-panel';
import { ThreadListItem } from '@/components/inbox/thread-list-item';
import { ThreadTimeline, IntegrationStateAlert } from '@/components/inbox/thread-timeline';
import { BulkActionsBar } from '@/components/inbox/bulk-actions-bar';
import { Checkbox } from '@/components/ui/checkbox';
import { useSendersWithCounts } from '@/hooks/useSenders';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useKeyboardShortcuts, type ShortcutConfig } from '@/hooks/useKeyboardShortcuts';
import { useAuth } from '@/contexts/AuthContext';
import { formatUiDateTime, formatUiWaitingDuration } from '@/lib/ui-format';
import { toSafeUiError } from '@/lib/user-safe-error';
import { invalidateReplyQueueMembership } from '@/lib/crm/reply-queue-cache';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface InboxStats {
  pending: number;
  approved_today: number;
  sent_today: number;
  avg_approval_time_minutes: number;
}

/** What to do once the user confirms discarding an unsaved draft edit. */
type PendingDiscardIntent =
  | { kind: 'thread'; thread: ConversationThread; index: number }
  | { kind: 'navigation'; href: string }
  | { kind: 'status'; status: ThreadStatus }
  | {
      kind: 'campaign';
      campaignId: string | 'all';
      urlAlreadyChanged: boolean;
      /**
       * DIFF round 4, F3: the exact `location.search` this intent was raised FOR. Without it, a
       * Back that returns to the already-accepted URL while the dialog is open leaves a stale
       * intent behind — and pressing the still-visible "Discard changes" then throws the edit away
       * for a transition that no longer exists. Present only for URL-driven intents; a dropdown
       * choice has not navigated yet.
       */
      urlSearch?: string;
    }
  | { kind: 'close' }
  | { kind: 'cancel' }
  | null;

interface PendingReplyGateOverride {
  source: 'generate-draft' | 'regenerate';
  rule?: string;
  draftId: string;
  customInstructions?: string;
  /** Defer only: false means the follow-up date was never stored, and the panel must say so. */
  followUpScheduled?: boolean;
}

const sameThreadFilters = (left: ThreadFilters, right: ThreadFilters): boolean =>
  left.status === right.status &&
  left.senderId === right.senderId &&
  left.campaignId === right.campaignId &&
  left.search === right.search &&
  left.page === right.page &&
  left.pageSize === right.pageSize;

const LINKEDIN_SEND_CONTAINMENT_NOTICE_ID =
  'linkedin-send-containment-notice';
const INBOX_LIST_PANEL_ID = 'inbox-conversation-list';
const INBOX_READING_PANEL_ID = 'inbox-reading-pane';
const INBOX_PANEL_IDS = [INBOX_LIST_PANEL_ID, INBOX_READING_PANEL_ID];
const INBOX_DEFAULT_LAYOUT: Record<string, number> = {
  [INBOX_LIST_PANEL_ID]: 35,
  [INBOX_READING_PANEL_ID]: 65,
};
const INBOX_RESIZE_TARGET_SIZE = { coarse: 44, fine: 12 };
const INBOX_LAYOUT_STORAGE = {
  getItem: (key: string): string | null => {
    if (typeof window === 'undefined') return null;
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key: string, value: string): void => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // A blocked storage API must not make the Inbox unusable; resizing still
      // works for the current session even when persistence is unavailable.
    }
  },
};

const supportsResizableInbox = (): boolean =>
  typeof window !== 'undefined' && typeof window.ResizeObserver === 'function';

const safeRecoveryMessage = (error: unknown): string => {
  const safeError = toSafeUiError(error);
  return `${safeError.description} ${safeError.recovery}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// API Hooks
// ─────────────────────────────────────────────────────────────────────────────

export const inboxStatsQueryKey = (
  userId: string | null,
  organizationId: string | null
): readonly ['linkedin-stats', 'today', string | null, string | null] =>
  ['linkedin-stats', 'today', userId, organizationId];

/**
 * Empty-state copy per tab. A function rather than a third ternary branch: `src/CLAUDE.md` allows
 * one level of nesting, and an empty Follow-up tab means something specific and good — nobody is
 * overdue — which "Try adjusting your filters" would misreport as a search problem.
 */
const emptyInboxCopy = (status: ThreadStatus): { title: string; body: string } => {
  if (status === 'needs_reply') {
    return { title: 'All caught up!', body: 'No conversations needing replies at the moment.' };
  }
  if (status === 'follow_up') {
    return {
      title: 'Nothing due yet',
      body: 'Deferred conversations appear here on the day their follow-up comes due, soonest first.',
    };
  }
  return { title: 'No conversations found', body: 'Try adjusting your filters or search.' };
};

function useInboxStats() {
  const { user, organizationId } = useAuth();
  const userId = user?.id ?? null;

  return useQuery({
    queryKey: inboxStatsQueryKey(userId, organizationId),
    queryFn: async () => {
      if (organizationId === null) return null;

      const today = new Date().toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('os_linkedin_draft_stats')
        .select(
          'pending, approved_today, sent_today, avg_approval_time_minutes, total_drafts'
        )
        .eq('organization_id', organizationId)
        .eq('date', today);

      if (error && error.code !== 'PGRST116') throw error;
      if (!data || data.length === 0) return null;

      const aggregated = data.reduce(
        (acc, row) => {
          acc.pending += row.pending || 0;
          acc.approved_today += row.approved_today || 0;
          acc.sent_today += row.sent_today || 0;

          const weight = row.total_drafts || 0;
          if (row.avg_approval_time_minutes !== null && weight > 0) {
            acc.avg_approval_time_total += row.avg_approval_time_minutes * weight;
            acc.avg_approval_time_weight += weight;
          }
          return acc;
        },
        {
          pending: 0,
          approved_today: 0,
          sent_today: 0,
          avg_approval_time_total: 0,
          avg_approval_time_weight: 0,
        }
      );

      return {
        pending: aggregated.pending,
        approved_today: aggregated.approved_today,
        sent_today: aggregated.sent_today,
        avg_approval_time_minutes: aggregated.avg_approval_time_weight
          ? aggregated.avg_approval_time_total / aggregated.avg_approval_time_weight
          : 0,
      } as InboxStats;
    },
    enabled: userId !== null && organizationId !== null,
    refetchInterval: 60000,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

export default function LinkedInInbox() {
  const campaignFilterDescriptionId = useId();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const inboxLayoutId = `inbox-workspace-${user?.id ?? 'unresolved'}`;
  const { defaultLayout, onLayoutChanged } = useResizableDefaultLayout({
    id: inboxLayoutId,
    panelIds: INBOX_PANEL_IDS,
    storage: INBOX_LAYOUT_STORAGE,
  });
  // R9 B4.6: source filter — URL-state, ready for post-cutover wiring
  const { filters: draftFilters, setFilters: setDraftFilters } = useLinkedInDraftFilters();
  const { filters: urlThreadFilters, setFilters: setThreadFilters } = useThreadFilters();
  // A browser Back/Forward transition updates the URL before this page can
  // ask about a dirty draft. Keep the complete data/action filter snapshot
  // on the last accepted campaign until that navigation is accepted through
  // applyCampaignChange; freezing only campaignId would still let a Back to
  // `status=reply_queue` clear the draft before the operator decides.
  const [threadFilters, setActiveThreadFilters] = useState(urlThreadFilters);
  const acceptedCampaignSearchRef = useRef(location.search);
  const pendingInternalCampaignRef = useRef<string | 'all' | null>(null);
  const isReplyQueueTab = threadFilters.status === 'reply_queue';
  const threadsQuery = useConversationThreads(threadFilters, { enabled: !isReplyQueueTab });
  const {
    data: threadsData,
    isLoading,
    isError,
    error,
    refetch,
    isPlaceholderData,
  } = threadsQuery;
  // The disabled query keeps serving its last placeholderData rather than
  // going empty (see useConversationThreads' `placeholderData: previousData`),
  // so `threads` must be forced empty here — every effect and render below
  // that treats "empty threads" as "nothing to auto-select" depends on it.
  const isCrossCampaignPlaceholder =
    isPlaceholderData && threadsData?.campaignId !== threadFilters.campaignId;
  const threads = useMemo(
    () => (
      isReplyQueueTab || isCrossCampaignPlaceholder ? [] : threadsData?.threads || []
    ),
    [isCrossCampaignPlaceholder, isReplyQueueTab, threadsData?.threads]
  );
  const { data: threadCounts, isLoading: threadCountsLoading } = useThreadCounts(
    threadFilters.senderId,
    threadFilters.campaignId
  );
  const {
    data: conversationCampaigns,
    isLoading: campaignsLoading,
    isFetching: campaignsIsFetching,
    isError: campaignsIsError,
    error: campaignsError,
    refetch: refetchCampaigns,
  } = useConversationCampaigns();
  const {
    data: draftCampaigns,
    isLoading: draftCampaignsLoading,
    isFetching: draftCampaignsIsFetching,
    isError: draftCampaignsIsError,
    error: draftCampaignsError,
    refetch: refetchDraftCampaigns,
  } = useDraftCampaignDomain();
  const campaignFilterOptions = isReplyQueueTab ? draftCampaigns : conversationCampaigns;
  const campaignFilterLoading = isReplyQueueTab ? draftCampaignsLoading : campaignsLoading;
  // DIFF r3 F1: `isLoading` is `isPending && isFetching`, so it is FALSE for the entire duration of
  // a background refetch on a query that already has data — it cannot tell "settled" from "settled
  // but currently being re-read". `isFetching` is a tracked boolean and is immune to the structural
  // sharing that keeps `data` at the same reference when a refetch returns an equal payload.
  const campaignFilterIsFetching = isReplyQueueTab
    ? draftCampaignsIsFetching
    : campaignsIsFetching;
  const campaignFilterIsError = isReplyQueueTab ? draftCampaignsIsError : campaignsIsError;
  const campaignFilterError = isReplyQueueTab ? draftCampaignsError : campaignsError;
  // The reconciliation read, as a PROMISE. This is the whole mechanism: the r2 attempt observed
  // completion through the effect's dependency array and none of the three candidate deps tick —
  // `data` keeps its reference under structural sharing, `isLoading` stays false while data exists,
  // and `dataUpdatedAt` does not move on an errored refetch nor when two successes land in the same
  // millisecond (the normal case under an instantly-resolving mock). `refetch()` never rejects and
  // resolves only after the observer has published the fresh result, so its promise is the one
  // honest completion signal available.
  const reconcileCampaignFilter = useCallback(
    (): Promise<unknown> => (isReplyQueueTab ? refetchDraftCampaigns() : refetchCampaigns()),
    [isReplyQueueTab, refetchDraftCampaigns, refetchCampaigns]
  );
  const refetchCampaignFilter = (): void => {
    void reconcileCampaignFilter();
  };


  const { data: stats } = useInboxStats();
  const { data: senders, isLoading: sendersLoading } = useSendersWithCounts();

  // Reply Queue tab — the drafts-truth view of unanswered inbound messages.
  // The count query is cheap (no message bodies) and always mounted, like
  // threadCounts, so the tab button can show a live badge even when another
  // tab is active. The full-row query is only enabled while the tab itself
  // is open — nobody needs 233 people's incoming messages in cache just to
  // render a badge number. Both poll every 60s so a focused Inbox tab can't
  // show an unboundedly stale queue.
  const [includeArchivedInReplyQueue, setIncludeArchivedInReplyQueue] = useState(false);
  const replyQueueFilters = {
    senderId: threadFilters.senderId,
    // Thread queries use the URL sentinel `all`; drafts-truth queries use
    // null for the same "no campaign predicate" operator intent.
    campaignId: threadFilters.campaignId === 'all' ? null : threadFilters.campaignId,
    includeArchived: includeArchivedInReplyQueue,
  };
  const replyQueueCountQuery = useReplyQueueCount(replyQueueFilters);
  const replyQueueRowsQuery = useReplyQueue(replyQueueFilters, { enabled: isReplyQueueTab });
  // Rows and count are separate server snapshots. While the queue is visible,
  // its badge comes from the exact rows snapshot being rendered so an uneven
  // refetch cannot briefly announce a number the list contradicts. The cheap
  // count query remains the badge source on every other tab.
  const replyQueueBadgeCount = isReplyQueueTab
    ? replyQueueRowsQuery.data?.rows.length
    : replyQueueCountQuery.data?.totalCount;
  const replyQueueBadgeIsLoading = isReplyQueueTab
    ? replyQueueRowsQuery.isLoading
    : replyQueueCountQuery.isLoading;
  const replyQueueBadgeIsError = isReplyQueueTab
    ? replyQueueRowsQuery.isError
    : replyQueueCountQuery.isError;

  // Alias for compatibility with existing code
  const filters = threadFilters;
  const setFilters = setThreadFilters;



  // Selected thread state
  const [selectedThread, setSelectedThread] = useState<ConversationThread | null>(null);

  // Thread messages for the selected thread — explicitly undefined while
  // Reply Queue is active so a resurrected selection can't repopulate a
  // stale draft (defense in depth alongside the `threads` derivation above).
  // The full query object (not just `.data`) is kept: a deep-linked target
  // (F3) must distinguish "still loading", "failed", and "genuinely no
  // drafts" instead of collapsing all three into the same empty `data`.
  const threadMessagesQuery = useThreadMessages(
    isReplyQueueTab ? undefined : selectedThread?.thread_id
  );
  const { data: threadMessages } = threadMessagesQuery;

  // F1 (round 14): ONE winner selected across the COMBINED live vocabulary
  // (src/hooks/useThreadMessages.ts's getLiveDraftWinner — the SAME
  // comparator the Reply Queue and Lead Dossier use), never two independent
  // status-partitioned picks with pending_approval given priority — that
  // shape let a generate/webhook race open an older pending draft while a
  // newer awaiting one existed. `activeDraft`/`awaitingDraft` below are
  // VIEWS of this single winner's status, not separate selections.
  // Dossier→Inbox carry: a `?draft=` param from the Lead Dossier's CTA
  // (src/pages/lead-dossier.tsx, mirroring leadDossierHref's Queue→Dossier
  // carry) constrains this SAME winner selection to that exact draft while
  // it is still live — a thread can legally hold more than one sender's
  // live draft, so the plain thread-wide winner could otherwise resurface a
  // DIFFERENT sender's draft than the one the operator clicked through from.
  // Filtering `threadMessages` self-scopes to the CURRENTLY loaded thread (a
  // draft id belongs to exactly one thread, so a stale param from a
  // since-abandoned deep link matches nothing here). No new selector:
  // `getLiveDraftWinner` is reused on the narrowed candidate set, and a null
  // result (draft no longer live) falls through to the normal thread-wide
  // winner — never a dead end.
  const carriedDraftIdParam = searchParams.get('draft');
  const carriedDraftMessages = carriedDraftIdParam && threadMessages
    ? threadMessages.filter((message) => message.id === carriedDraftIdParam)
    : [];
  const carriedLiveDraftWinner =
    carriedDraftMessages.length > 0 ? getLiveDraftWinner(carriedDraftMessages) : null;
  const liveDraftWinner = threadMessages
    ? carriedLiveDraftWinner ?? getLiveDraftWinner(threadMessages)
    : null;
  const activeDraft = liveDraftWinner?.status === 'pending_approval' ? liveDraftWinner : null;
  const awaitingDraft = liveDraftWinner?.status === 'awaiting_draft' ? liveDraftWinner : null;

  const [selectedDraft, setSelectedDraft] = useState<DraftListItem | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editedMessage, setEditedMessage] = useState('');
  const [selectedAlternativeIndex, setSelectedAlternativeIndex] = useState(0);
  const [suggestionAccepted, setSuggestionAccepted] = useState(true);
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
  const [workspaceIsWide, setWorkspaceIsWide] = useState(false);
  const [localSearch, setLocalSearch] = useState(filters.search);
  const [regenInstruction, setRegenInstruction] = useState('');
  const [showRegenInput, setShowRegenInput] = useState(false);
  const [showAiReasoning, setShowAiReasoning] = useState(false);
  const [pendingReplyGateOverrides, setPendingReplyGateOverrides] = useState<
    PendingReplyGateOverride[]
  >([]);
  const [rejectConfirmationOpen, setRejectConfirmationOpen] = useState(false);
  const [discardConfirmationOpen, setDiscardConfirmationOpen] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [pendingDeleteThreadIds, setPendingDeleteThreadIds] = useState<string[]>([]);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // A single discriminated union instead of four independent nullable
  // fields: those could disagree (e.g. a stale pendingStatusSwitch left set
  // after a `pendingDiscardAction: 'cancel'` flow closed the dialog without
  // clearing it), and confirmDiscardChanges would then act on whichever
  // stale field it checked first — jumping to a tab the user never
  // requested. One field makes that combination unrepresentable.
  const [pendingDiscardIntent, setPendingDiscardIntent] = useState<PendingDiscardIntent>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionStatus, setActionStatus] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const workspaceRef = useRef<HTMLDivElement>(null);
  const approveInFlightRef = useRef(false);
  const generateInFlightRef = useRef(false);
  const regenerateInFlightRef = useRef(false);
  const replyGateOverrideInFlightRef = useRef(false);
  const deleteInFlightRef = useRef(false);
  const deleteTriggerRef = useRef<HTMLButtonElement | null>(null);
  const editBaselineRef = useRef('');
  const openedDeepLinkRef = useRef<string | null>(null);
  const detailOpenerRef = useRef<HTMLButtonElement | null>(null);
  useLayoutEffect(() => {
    const workspace = workspaceRef.current;
    if (!workspace || !supportsResizableInbox()) return;

    const updateWorkspaceMode = (width: number): void => {
      const nextIsWide = width >= 900;
      setWorkspaceIsWide((currentIsWide) => (
        currentIsWide === nextIsWide ? currentIsWide : nextIsWide
      ));
    };
    updateWorkspaceMode(workspace.getBoundingClientRect().width);

    const observer = new window.ResizeObserver((entries) => {
      const entry = entries[0];
      updateWorkspaceMode(
        entry?.contentRect.width ?? workspace.getBoundingClientRect().width
      );
    });
    observer.observe(workspace);
    return () => observer.disconnect();
  }, []);
  const isDraftDirty = isEditing && editedMessage !== editBaselineRef.current;
  // Reply Queue explicitly owns its own tab body — never render a (possibly
  // stale, still-placeholder-backed) draft detail alongside it.
  const showDraftDetail = !isReplyQueueTab && !!selectedDraft;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const regenerateMutation = useRegenerateDraft();
  const generateMutation = useGenerateDraft();
  // Dedicated observers keep an override click from replacing the per-call callbacks of an
  // ordinary Generate/Regenerate already running on a newly selected row.
  const replyGateRegenerateOverrideMutation = useRegenerateDraft();
  const replyGateGenerateOverrideMutation = useGenerateDraft();
  const softDeleteMutation = useSoftDeleteThreads();
  const restoreMutation = useRestoreThreads();
  const bulkResolveMutation = useBulkResolveThreads();
  const dismissFollowUpMutation = useDismissFollowUp();
  const pendingReplyGateOverride = pendingReplyGateOverrides[0] ?? null;
  const pendingReplyGateOverrideCopy = pendingReplyGateOverride
    ? describeGateBlockWithOverride(
        pendingReplyGateOverride.rule,
        pendingReplyGateOverride.followUpScheduled
      )
    : null;
  const replyGateOverrideIsPending =
    replyGateGenerateOverrideMutation.isPending ||
    replyGateRegenerateOverrideMutation.isPending;

  // Full conversation history from HeyReach
  const { data: conversationHistory, isLoading: historyLoading } =
    useConversationHistory(selectedThread?.thread_id, !!selectedThread);

  // Bulk selection state
  const [selectedThreadIds, setSelectedThreadIds] = useState<Set<string>>(new Set());
  const toggleThreadSelection = (threadId: string): void => {
    setSelectedThreadIds(prev => {
      const next = new Set(prev);
      if (next.has(threadId)) next.delete(threadId);
      else next.add(threadId);
      return next;
    });
  };
  const clearSelection = useCallback((): void => setSelectedThreadIds(new Set()), []);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
  const [isAllAcrossPages, setIsAllAcrossPages] = useState(false);
  const [stageFilter, setStageFilter] = useState<string[]>([]);

  const filteredThreads = useMemo(() => {
    if (stageFilter.length === 0) return threads;
    return threads.filter((t) => stageFilter.includes(t.current_stage));
  }, [threads, stageFilter]);

  // Placeholder rows are useful while paging, but they are not the result for
  // the current request yet. Mutations are therefore bound to a settled page,
  // and (when a campaign is selected) to rows that carry that exact campaign.
  // This is independent of what happens to be retained in React state.
  const actionableThreadIds = useMemo(() => {
    if (isReplyQueueTab || isPlaceholderData) return new Set<string>();
    return new Set(
      threads
        .filter(
          (thread) =>
            filters.campaignId === 'all' ||
            thread.heyreach_campaign_id === filters.campaignId
        )
        .map((thread) => thread.thread_id)
    );
  }, [filters.campaignId, isPlaceholderData, isReplyQueueTab, threads]);
  const selectedThreadIdsAreActionable =
    selectedThreadIds.size > 0 &&
    Array.from(selectedThreadIds).every((threadId) => actionableThreadIds.has(threadId));

  const allPageSelected =
    selectedThreadIdsAreActionable &&
    filteredThreads.length > 0 &&
    filteredThreads.every((t) => selectedThreadIds.has(t.thread_id));
  const someSelected = selectedThreadIdsAreActionable;

  const handleSelectAll = (checked: boolean | 'indeterminate'): void => {
    if (isPlaceholderData) return;
    if (checked) {
      setSelectedThreadIds(new Set(filteredThreads.map((t) => t.thread_id)));
    } else {
      setSelectedThreadIds(new Set());
      setIsAllAcrossPages(false);
    }
  };

  const handleThreadSelect = (threadId: string, index: number, shiftKey: boolean): void => {
    if (!actionableThreadIds.has(threadId)) return;
    if (shiftKey && lastSelectedIndex !== null) {
      const from = Math.min(lastSelectedIndex, index);
      const to = Math.max(lastSelectedIndex, index);
      const rangeIds = filteredThreads.slice(from, to + 1).map((t) => t.thread_id);
      setSelectedThreadIds((prev) => new Set([...prev, ...rangeIds]));
    } else {
      toggleThreadSelection(threadId);
      setLastSelectedIndex(index);
    }
  };

  useDocumentTitle('Inbox', threadCounts?.needs_reply);

  const debouncedSearch = useDebouncedCallback((value: string) => {
    setFilters({ search: value });
  }, 300);

  useEffect(() => {
    setLocalSearch(filters.search);
  }, [filters.search]);

  // Sync the single combined live winner before considering history. Both
  // live statuses must drive the display; history is only a no-live fallback.
  useEffect(() => {
    if (liveDraftWinner) {
      setSelectedDraft(liveDraftWinner as unknown as DraftListItem);
    } else if (selectedThread && threadMessages && threadMessages.length > 0) {
      // No active draft, but thread has messages - select the latest one for viewing
      const latest = threadMessages[threadMessages.length - 1];
      setSelectedDraft(latest as unknown as DraftListItem);
    } else {
      setSelectedDraft(null);
    }
  }, [liveDraftWinner, selectedThread, threadMessages]);

  // Reply Queue tab bypasses the status-column-backed thread list entirely
  // (the disabled useConversationThreads query keeps serving its last
  // placeholderData rather than going empty), so the usual "threads is
  // empty" auto-select/reset logic below never fires for this tab. Clear the
  // inline detail explicitly instead of relying on that side effect.
  useEffect(() => {
    if (!isReplyQueueTab) return;
    setSelectedThread(null);
    setSelectedDraft(null);
    setMobileDetailOpen(false);
    setIsEditing(false);
    setActionError(null);
    setActionStatus(null);
  }, [isReplyQueueTab]);

  // Fallback for a deep-linked thread (`?thread=<id>`, e.g. from the lead
  // dossier's "Review Draft" action) whose status doesn't match the current
  // filter — `threads` below never contains it, so without this it would be
  // silently unselectable rather than merely absent from the visible list.
  //
  // `needsDeepLinkFallback` is an explicit "unresolved target" state, true
  // from the moment the URL names an out-of-list id until it is EITHER
  // committed (openedDeepLinkRef records it) OR the list itself starts
  // carrying it (filter changed). It must gate the auto-select/index-resync
  // effects below for its own entire pending/error/not-found lifetime, not
  // only after a selection has already succeeded — otherwise those effects
  // fall through to threads[0] while resolution is still in flight, and an
  // operator can act on an unrelated draft under a URL that looks targeted.
  const deepLinkedThreadIdParam = searchParams.get('thread');
  const needsDeepLinkFallback =
    !isReplyQueueTab &&
    !!deepLinkedThreadIdParam &&
    openedDeepLinkRef.current !== deepLinkedThreadIdParam &&
    !actionableThreadIds.has(deepLinkedThreadIdParam);
  const deepLinkFallbackQuery = useThreadById(
    !isReplyQueueTab &&
      deepLinkedThreadIdParam &&
      !actionableThreadIds.has(deepLinkedThreadIdParam)
      ? deepLinkedThreadIdParam
      : null
  );
  const deepLinkFallbackThread = deepLinkFallbackQuery.data;

  // True once the deep-link effect below has committed a selection for this
  // target (in-filter or via fallback) — independent of whether the thread
  // ended up inside or outside the active filter. Stays true until the user
  // navigates to a different thread (openedDeepLinkRef never changes once set).
  const isDeepLinkTarget = !!selectedThread && openedDeepLinkRef.current === selectedThread.thread_id;

  // List and bulk actions remain tied to the settled current page. A resolved
  // explicit deep link is the one safe single-thread exception: its own
  // active-organization lookup proves the target independently of status,
  // campaign and page membership. Keeping the fallback query mounted after
  // commit preserves that proof without trusting placeholder rows.
  const selectedThreadIsResolvedDeepLink =
    !isReplyQueueTab &&
    !!selectedThread &&
    !!deepLinkedThreadIdParam &&
    openedDeepLinkRef.current === deepLinkedThreadIdParam &&
    selectedThread.thread_id === deepLinkedThreadIdParam &&
    deepLinkFallbackQuery.isSuccess &&
    deepLinkFallbackThread?.thread_id === deepLinkedThreadIdParam;
  const isMutationTargetActionable = (threadId: string): boolean =>
    actionableThreadIds.has(threadId) ||
    (
      selectedThreadIsResolvedDeepLink &&
      selectedThread?.thread_id === threadId
    );
  const selectedThreadIsActionable =
    !!selectedThread &&
    isMutationTargetActionable(selectedThread.thread_id);

  // Detail-pane-facing resolution state. F3: target resolution is really
  // TWO steps — the thread (deepLinkFallbackQuery), then that thread's own
  // drafts (threadMessagesQuery) — and both must reach a terminal state
  // before falling through to normal rendering. Computed fresh every render
  // straight from each query's own status — never just `.data` — so
  // loading/error/successful-empty are each distinct states instead of
  // being discarded down to a generic "no draft selected".
  //
  // The draft-empty check reads `threadMessages` directly (the query's raw
  // data), not the effect-derived `selectedDraft` a render below still
  // computes — `selectedDraft` updates one render later, so checking it
  // here would flash "no draft found" for a real draft that just hasn't
  // been synced into state yet.
  //
  // F4: for the SAME reason, a successful non-empty `threadMessagesQuery`
  // must not immediately hand the render back to the normal
  // showDraftDetail branch — `selectedDraft` (and therefore
  // `showDraftDetail`) is still one render behind on the very first render
  // where `threadMessagesQuery` flips to success, so that render would
  // otherwise fall through to the generic "No draft selected" for exactly
  // one committed frame. Stay in 'draft-loading' (still true: the display
  // draft genuinely isn't ready to show yet) until `showDraftDetail`
  // actually catches up.
  let deepLinkResolutionState: 'none' | 'loading' | 'error' | 'not-found' | 'draft-loading' | 'draft-error' | 'draft-empty' = 'none';
  if (needsDeepLinkFallback) {
    if (deepLinkFallbackQuery.isError) {
      deepLinkResolutionState = 'error';
    } else if (deepLinkFallbackQuery.isSuccess && deepLinkFallbackQuery.data === null) {
      deepLinkResolutionState = 'not-found';
    } else {
      deepLinkResolutionState = 'loading';
    }
  } else if (isDeepLinkTarget) {
    if (threadMessagesQuery.isError) {
      deepLinkResolutionState = 'draft-error';
    } else if (threadMessagesQuery.isSuccess && (!threadMessages || threadMessages.length === 0)) {
      deepLinkResolutionState = 'draft-empty';
    } else if (!threadMessagesQuery.isSuccess || !showDraftDetail) {
      deepLinkResolutionState = 'draft-loading';
    }
  }

  // True while the selected thread was reached via a deep link whose target
  // lies outside the current status filter — the auto-select and
  // index-resync effects below must stand down, or they'd bounce this
  // correctly-selected thread back to threads[0] because it isn't in
  // `threads` (see `deepLinkFallbackThread` above).
  const isDeepLinkOverrideActive =
    isDeepLinkTarget &&
    !!selectedThread &&
    !actionableThreadIds.has(selectedThread.thread_id);

  // Either an unresolved target is pending/errored/not-found, or one already
  // resolved outside the filter — both must block the effects below.
  const deepLinkGateActive = needsDeepLinkFallback || isDeepLinkOverrideActive;

  // Auto-select first thread. Gated by isReplyQueueTab as defense in depth —
  // `threads` is already forced empty on that tab above, but this effect
  // must never be the thing that resurrects a selection there.
  useEffect(() => {
    if (isReplyQueueTab || deepLinkGateActive) return;
    if (threads.length === 0) {
      setSelectedThread(null);
      setSelectedIndex(0);
      return;
    }

    const existing = selectedThread
      ? threads.find((t) => t.thread_id === selectedThread.thread_id)
      : null;

    if (!existing) {
      setSelectedThread(threads[0] || null);
      setSelectedIndex(0);
    }
  }, [threads, selectedThread, isReplyQueueTab, deepLinkGateActive]);

  // F2: a resolved deep-link target's `selectedIndex` never gets synced
  // while it's out-of-filter — the deep-link commit effect below only sets
  // it when `threads.findIndex(...) >= 0`, which is -1 for a fallback
  // target, so `selectedIndex` is left at its prior value (usually 0). The
  // moment the operator switches to the target's real status filter (`?
  // thread=` is preserved by setFilters), the target enters `threads` and
  // `isDeepLinkOverrideActive` drops, ungating this effect on the SAME
  // render — with a STALE selectedIndex. Without the branch below, this
  // effect would then overwrite `selectedThread` with `threads[0]`,
  // bouncing away from the correctly-selected target while the URL still
  // names it. `isDeepLinkTarget` is keyed on `openedDeepLinkRef`/
  // `selectedThread`, not on list membership, so it stays true across
  // exactly this transition: sync `selectedIndex` TO the target's real
  // position instead, and never touch `selectedThread` for as long as it
  // still IS the deep-link target.
  useEffect(() => {
    if (isReplyQueueTab || deepLinkGateActive) return;
    if (isDeepLinkTarget) {
      const targetIndex = threads.findIndex((t) => t.thread_id === selectedThread?.thread_id);
      if (targetIndex >= 0 && targetIndex !== selectedIndex) {
        setSelectedIndex(targetIndex);
      }
      return;
    }
    if (threads[selectedIndex] && threads[selectedIndex]?.thread_id !== selectedThread?.thread_id) {
      setSelectedThread(threads[selectedIndex] || null);
    }
  }, [threads, selectedThread, selectedIndex, isReplyQueueTab, deepLinkGateActive, isDeepLinkTarget]);

  useEffect(() => {
    if (isReplyQueueTab) return;
    const deepLinkedThreadId = searchParams.get('thread');
    if (!deepLinkedThreadId || openedDeepLinkRef.current === deepLinkedThreadId) return;
    const deepLinkedThread = actionableThreadIds.has(deepLinkedThreadId)
      ? threads.find((thread) => thread.thread_id === deepLinkedThreadId) ?? null
      : deepLinkFallbackQuery.isSuccess &&
          deepLinkFallbackThread?.thread_id === deepLinkedThreadId
        ? deepLinkFallbackThread
        : null;
    if (!deepLinkedThread) return;

    openedDeepLinkRef.current = deepLinkedThreadId;
    const index = threads.findIndex((thread) => thread.thread_id === deepLinkedThreadId);
    setSelectedThread(deepLinkedThread);
    if (index >= 0) setSelectedIndex(index);

    const containerWidth = workspaceRef.current?.getBoundingClientRect().width;
    if (containerWidth !== undefined && containerWidth < 900) {
      setMobileDetailOpen(true);
    }
  }, [
    actionableThreadIds,
    deepLinkFallbackQuery.isSuccess,
    deepLinkFallbackThread,
    isReplyQueueTab,
    searchParams,
    threads,
  ]);

  // Update edited message when draft changes
  useEffect(() => {
    if (selectedDraft) {
      const alternatives = toDraftAlternatives(selectedDraft);
      const initialIndex = selectedDraft.selected_alternative ?? 0;
      const selectedAlternative = getSelectedAlternative(alternatives, initialIndex);
      const fallbackMessage = selectedDraft.edited_response || selectedDraft.draft_response;
      const initialMessage =
        selectedDraft.edited_response || selectedAlternative?.text || fallbackMessage;
      setSelectedAlternativeIndex(initialIndex);
      setEditedMessage(initialMessage);
      editBaselineRef.current = initialMessage;
      setSuggestionAccepted(true);
      setIsEditing(false);
      setActionError(null);
    }
  }, [selectedDraft]);

  useEffect(() => {
    clearSelection();
    setIsAllAcrossPages(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageFilter]);

  useEffect(() => {
    if (!selectedDraft) return;
    const alternatives = toDraftAlternatives(selectedDraft);
    if (alternatives.length === 0) return;
    if (selectedAlternativeIndex >= alternatives.length) {
      setSelectedAlternativeIndex(0);
    }
  }, [selectedAlternativeIndex, selectedDraft]);

  useEffect(() => {
    if (!isDraftDirty) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      event.returnValue = '';
    };
    const handleInternalNavigation = (event: MouseEvent): void => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest<HTMLAnchorElement>('a[href]');
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;

      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      const nextHref = `${url.pathname}${url.search}${url.hash}`;
      const currentHref = `${location.pathname}${location.search}${location.hash}`;
      if (nextHref === currentHref) return;

      event.preventDefault();
      event.stopPropagation();
      setPendingDiscardIntent({ kind: 'navigation', href: nextHref });
      setDiscardConfirmationOpen(true);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('click', handleInternalNavigation, true);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('click', handleInternalNavigation, true);
    };
  }, [isDraftDirty, location.hash, location.pathname, location.search]);

  // ───────────────────────────────────────────────────────────────────────────
  // Mutations
  // ───────────────────────────────────────────────────────────────────────────

  const approveMutation = useMutation({
    mutationFn: async ({
      draft,
      message,
      selectedAlternative,
      editDiff,
    }: {
      draft: DraftListItem;
      message: string;
      selectedAlternative: number | null;
      editDiff: string | null;
      threadId: string | null;
    }) => {
      if (isLinkedInSendContained()) {
        throw new Error(LINKEDIN_SEND_CONTAINMENT_MESSAGE);
      }

      const draftId = draft.id;
      // Compare-and-set on the status this approval is allowed to come FROM. Without it the update
      // was keyed on `id` alone, so any row the UI happened to surface could be moved to `approved`
      // and then sent — including one the reply gate REJECTED, whose draft_response survives the
      // refusal untouched. That is the one thing this product must never do on its own: the gate's
      // "no" would have been overturned with no override, no audit and no human decision.
      //
      // `approved` and `failed` stay in the allowed set on purpose — a send that failed is retried
      // from exactly those states, and this guard must not turn a transient HeyReach error into a
      // draft nobody can ever send.
      const { data: approvedRows, error: updateError } = await supabase
        .from('os_linkedin_drafts')
        .update({
          status: 'approved',
          edited_response: message !== draft.draft_response ? message : null,
          selected_alternative: selectedAlternative ?? 0,
          human_edit_diff: editDiff,
          approved_at: new Date().toISOString(),
        })
        .eq('id', draftId)
        .in('status', ['pending_approval', 'approved', 'failed'])
        .select('id');
      
      if (updateError) throw updateError;
      if (!approvedRows || approvedRows.length !== 1) {
        throw new Error(
          'This draft is not awaiting approval — the reply gate closed it, or it was already sent. Use "Draft anyway" to overrule the gate.'
        );
      }
      
      // Send via HeyReach
      const sendResult = await sendReply({
        draftId,
        message,
      });
      
      if (!sendResult.success) {
        // Mark as failed
        await supabase
          .from('os_linkedin_drafts')
          .update({
            status: 'failed',
            send_error: sendResult.error,
          })
          .eq('id', draftId);
        
        throw new Error(sendResult.error);
      }
      
      // Mark as sent
      await supabase
        .from('os_linkedin_drafts')
        .update({
          status: 'sent',
          sent_at: new Date().toISOString(),
        })
        .eq('id', draftId);

      await logBrainLearning({
        draft,
        outcome: editDiff ? 'approved_with_edit' : 'approved',
        editDiff,
      });
      
      return sendResult;
    },
    onSuccess: (_, variables) => {
      toast({
        title: 'Message sent',
        description: 'LinkedIn message sent successfully via HeyReach',
      });
      setActionError(null);
      setActionStatus('Message sent successfully.');
      if (variables.threadId) {
        queryClient.setQueriesData<ThreadQueryResult>(
          { queryKey: ['conversation-threads'] },
          (current) => {
            if (!current) return current;
            const threads = current.threads.filter(
              (thread) => thread.thread_id !== variables.threadId
            );
            return {
              ...current,
              threads,
              total: Math.max(0, current.total - (threads.length < current.threads.length ? 1 : 0)),
            };
          }
        );
      }
      queryClient.invalidateQueries({ queryKey: ['linkedin-drafts'] });
      queryClient.invalidateQueries({ queryKey: ['linkedin-draft-counts'] });
      queryClient.invalidateQueries({ queryKey: ['conversation-threads'] });
      queryClient.invalidateQueries({ queryKey: ['thread-messages'] });
      queryClient.invalidateQueries({ queryKey: ['thread-counts'] });
      queryClient.invalidateQueries({ queryKey: ['linkedin-stats'] });
      setSelectedThread(null);
      setSelectedDraft(null);
      setMobileDetailOpen(false);
      setIsEditing(false);
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error &&
        error.message === LINKEDIN_SEND_CONTAINMENT_MESSAGE
          ? LINKEDIN_SEND_CONTAINMENT_MESSAGE
          : safeRecoveryMessage(error);
      setActionError(message);
      setActionStatus(null);
      toast({
        title: 'Failed to send',
        description: message,
        variant: 'destructive',
      });
    },
    onSettled: () => {
      approveInFlightRef.current = false;
    },
  });
  
  const rejectMutation = useMutation({
    mutationFn: async ({
      draft,
    }: {
      draft: DraftListItem;
      threadId: string | null;
    }) => {
      const { error } = await supabase
        .from('os_linkedin_drafts')
        .update({
          status: 'rejected',
          approved_at: new Date().toISOString(),
        })
        .eq('id', draft.id);
      
      if (error) throw error;

      await logBrainLearning({
        draft,
        outcome: 'rejected',
        editDiff: null,
      });
    },
    onSuccess: (_, variables) => {
      toast({
        title: 'Draft rejected',
        description: 'Draft removed from queue',
      });
      setActionError(null);
      setActionStatus('Draft rejected and removed from the pending queue.');
      if (variables.threadId) {
        queryClient.setQueriesData<ThreadQueryResult>(
          { queryKey: ['conversation-threads'] },
          (current) => {
            if (!current) return current;
            const threads = current.threads.filter(
              (thread) => thread.thread_id !== variables.threadId
            );
            return {
              ...current,
              threads,
              total: Math.max(0, current.total - (threads.length < current.threads.length ? 1 : 0)),
            };
          }
        );
      }
      queryClient.invalidateQueries({ queryKey: ['linkedin-drafts'] });
      queryClient.invalidateQueries({ queryKey: ['linkedin-draft-counts'] });
      queryClient.invalidateQueries({ queryKey: ['conversation-threads'] });
      queryClient.invalidateQueries({ queryKey: ['thread-messages'] });
      queryClient.invalidateQueries({ queryKey: ['thread-counts'] });
      setRejectConfirmationOpen(false);
      setSelectedThread(null);
      setSelectedDraft(null);
      setMobileDetailOpen(false);
    },
    onError: (error: unknown) => {
      const message = safeRecoveryMessage(error);
      setActionError(message);
      setRejectConfirmationOpen(false);
      toast({
        title: 'Failed to reject draft',
        description: message,
        variant: 'destructive',
      });
    },
    // Reject moves a live pending draft out of Reply Queue membership.
    onSettled: () => invalidateReplyQueueMembership(queryClient),
  });
  
  // ───────────────────────────────────────────────────────────────────────────
  // Actions
  // ───────────────────────────────────────────────────────────────────────────
  
  const handleApprove = () => {
    if (!selectedThreadIsActionable) return;
    if (isLinkedInSendContained()) {
      setActionError(null);
      setActionStatus(LINKEDIN_SEND_CONTAINMENT_MESSAGE);
      toast({
        title: 'Sending unavailable',
        description: LINKEDIN_SEND_CONTAINMENT_MESSAGE,
      });
      return;
    }

    if (!selectedDraft || approveInFlightRef.current || approveMutation.isPending) return;
    const alternatives = toDraftAlternatives(selectedDraft);
    const selectedAlternative = getSelectedAlternative(alternatives, selectedAlternativeIndex);
    const resolvedMessage = suggestionAccepted
      ? editedMessage
      : selectedAlternative?.text || editedMessage;

    if (!suggestionAccepted && selectedAlternative) {
      setEditedMessage(selectedAlternative.text);
      setSuggestionAccepted(true);
    }

    // Validate message
    const validation = validateLinkedInMessage(resolvedMessage);
    if (!validation.valid) {
      toast({
        title: 'Invalid message',
        description: validation.errors.join(', '),
        variant: 'destructive',
      });
      return;
    }
    
    approveInFlightRef.current = true;
    setActionError(null);
    setActionStatus(null);
    approveMutation.mutate({
      draft: selectedDraft,
      message: resolvedMessage,
      selectedAlternative: selectedAlternativeIndex,
      editDiff: buildEditDiff(selectedDraft.draft_response, resolvedMessage),
      threadId: selectedThread?.thread_id ?? null,
    });
  };
  
  const handleReject = () => {
    if (!selectedDraft || !selectedThreadIsActionable) return;
    setRejectConfirmationOpen(true);
  };

  const confirmReject = (): void => {
    if (!selectedDraft || !selectedThreadIsActionable || rejectMutation.isPending) return;
    setActionError(null);
    rejectMutation.mutate({
      draft: selectedDraft,
      threadId: selectedThread?.thread_id ?? null,
    });
  };
  
  const handleEdit = () => {
    editBaselineRef.current = editedMessage;
    setActionError(null);
    setIsEditing(true);
    setSuggestionAccepted(true);
  };
  
  const resetDraftEdits = (): void => {
    setIsEditing(false);
    if (!selectedDraft) return;
    const alternatives = toDraftAlternatives(selectedDraft);
    const selectedAlternative = getSelectedAlternative(alternatives, selectedAlternativeIndex);
    const originalMessage =
      selectedDraft.edited_response ||
      selectedAlternative?.text ||
      selectedDraft.draft_response ||
      '';
    setEditedMessage(originalMessage);
    editBaselineRef.current = originalMessage;
    setSuggestionAccepted(true);
  };

  const handleCancelEdit = (): void => {
    if (!isDraftDirty) {
      resetDraftEdits();
      return;
    }
    setPendingDiscardIntent({ kind: 'cancel' });
    setDiscardConfirmationOpen(true);
  };

  const focusDetailOpener = (): void => {
    window.requestAnimationFrame(() => detailOpenerRef.current?.focus());
  };

  const selectThreadNow = (
    thread: ConversationThread,
    index: number,
    opener?: HTMLButtonElement
  ): void => {
    if (opener) {
      detailOpenerRef.current = opener;
    }
    setSelectedThread(thread);
    setSelectedIndex(index);
    setActionError(null);
    setActionStatus(null);
    const containerWidth = workspaceRef.current?.getBoundingClientRect().width;
    if (containerWidth !== undefined && containerWidth < 900) {
      setMobileDetailOpen(true);
    }
  };

  const requestThreadSelection = (
    thread: ConversationThread,
    index: number,
    opener: HTMLButtonElement
  ): void => {
    if (!actionableThreadIds.has(thread.thread_id)) return;
    if (isDraftDirty && thread.thread_id !== selectedThread?.thread_id) {
      setPendingDiscardIntent({ kind: 'thread', thread, index });
      setDiscardConfirmationOpen(true);
      return;
    }
    selectThreadNow(thread, index, opener);
  };

  const requestDetailClose = (): void => {
    if (isDraftDirty) {
      setPendingDiscardIntent({ kind: 'close' });
      setDiscardConfirmationOpen(true);
      return;
    }
    setMobileDetailOpen(false);
    focusDetailOpener();
  };

  // Page-owned guarded navigation for the Reply Queue tab: both the matched
  // (ThreadListItem/button) and fallback rows call this instead of
  // navigating directly, so an unsaved draft edit always gets the same
  // discard confirmation the rest of the page uses for `<a href>` clicks.
  const requestGuardedNavigation = (href: string): void => {
    if (isDraftDirty) {
      setPendingDiscardIntent({ kind: 'navigation', href });
      setDiscardConfirmationOpen(true);
      return;
    }
    navigate(href);
  };

  const applyStatusChange = (status: ThreadStatus): void => {
    // Entering or leaving Reply Queue: clear the inline-detail selection
    // explicitly rather than relying on the destination tab's own query to
    // resolve first — the queue-entry effect already does this on entry,
    // this covers the leaving side too. Other tab-to-tab switches keep their
    // existing behavior (the auto-select effect swaps in the new tab's
    // first thread) unchanged.
    if (status === 'reply_queue' || filters.status === 'reply_queue') {
      setSelectedThread(null);
      setSelectedDraft(null);
    }
    // DIFF r4 F3 — the eager clear that used to live here is GONE, and its absence is the fix.
    //
    // It decided from whatever snapshot the destination domain happened to hold, and
    // `useDraftCampaignDomain` carries a 30s staleTime with nothing invalidating it when a draft
    // arrives. So a campaign that had just earned its first draft was absent from a warm-but-stale
    // drafts snapshot, and clicking Reply Queue with it selected wrote `campaign=all` immediately —
    // after which the reactive normalizer exited on its own first guard, because the filter was
    // already `all`. One bounded reconciliation was the whole contract, and this path skipped it.
    //
    // The reactive effect below is the single decision point now: it waits for `isFetching` to
    // settle, performs ONE reconciliation read, and clears only if that completed read still omits
    // the id. Deciding in two places is what let a stale snapshot win.
    setFilters({ status });
    clearSelection();
    setIsAllAcrossPages(false);
  };

  const applyCampaignChange = useCallback((
    campaignId: string | 'all',
    syncUrl = true,
    nextFilters?: ThreadFilters,
    acceptedSearch?: string
  ): void => {
    if (campaignId === filters.campaignId) return;

    // One synchronous transition owns every campaign-bound, action-bearing
    // state. The URL changes only after all A selections and pending actions
    // have been cleared, so no render under campaign B can carry A ids.
    setSelectedThread(null);
    setSelectedDraft(null);
    setSelectedIndex(0);
    clearSelection();
    setLastSelectedIndex(null);
    setIsAllAcrossPages(false);
    setMobileDetailOpen(false);
    setIsEditing(false);
    setPendingDeleteThreadIds([]);
    setDeleteConfirmationOpen(false);
    setRejectConfirmationOpen(false);
    setDiscardConfirmationOpen(false);
    setPendingDiscardIntent(null);
    setRegenInstruction('');
    setShowRegenInput(false);
    setActionError(null);
    setActionStatus(null);
    // A preserved `?thread=` is unresolved in the new campaign scope until
    // the commit effect proves it through a settled row or useThreadById.
    // Marking it resolved here would let auto-selection choose an unrelated
    // campaign row while the URL continued naming the old target.
    openedDeepLinkRef.current = null;
    setActiveThreadFilters(nextFilters ?? { ...filters, campaignId, page: 1 });
    if (acceptedSearch !== undefined) {
      acceptedCampaignSearchRef.current = acceptedSearch;
    }
    if (syncUrl) {
      pendingInternalCampaignRef.current = campaignId;
      setFilters({ campaignId });
    } else {
      pendingInternalCampaignRef.current = null;
    }
  }, [clearSelection, filters, setFilters]);

  const handleCampaignChange = (campaignId: string | 'all'): void => {
    if (campaignId === filters.campaignId) return;
    if (isDraftDirty) {
      setPendingDiscardIntent({ kind: 'campaign', campaignId, urlAlreadyChanged: false });
      setDiscardConfirmationOpen(true);
      return;
    }
    applyCampaignChange(campaignId);
  };

  /**
   * Normalization path for an invalid campaign parameter. Keeps `applyCampaignChange`'s dirty-draft
   * protection and accepted-campaign bookkeeping (the effect that calls this already returns early
   * on a dirty draft), but writes the URL with `replaceHistory` so no history entry is created.
   */
  const normalizeCampaignToAll = (): void => {
    setFilters({ campaignId: 'all', replaceHistory: true });
  };

  // DIFF r2 F1. Resetting the campaign inside `applyStatusChange` only covers the case where the
  // destination domain is ALREADY loaded at click time. Three ordinary paths slip past it and land
  // on an active filter with no name: switching tabs before `useDraftCampaignDomain` resolves,
  // opening a stale bookmark like `/inbox?status=reply_queue&campaign=<removed-id>` (which never
  // calls applyStatusChange at all), and a domain read that fails and only succeeds on Retry. In
  // each, rows and count faithfully send the unknown id to the server and render a convincing
  // empty list with a blank trigger — a filter the operator cannot see, name, or clear.
  //
  // So normalize reactively, once the ACTIVE domain has actually settled. Loading and error are
  // never treated as absence: an id missing from a domain that has not arrived is unknown, not gone.
  const campaignDomainKind = isReplyQueueTab ? 'drafts' : 'threads';
  const attemptedCampaignNormalizationRef = useRef<string | null>(null);
  const [campaignNormalizationPending, setCampaignNormalizationPending] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (filters.campaignId === 'all') return;
    // A dirty draft means a campaign transition is already mid-negotiation: the operator is being
    // asked to keep or discard, and `applyCampaignChange` owns which snapshot is accepted. Firing
    // a normalization into that would answer the question on their behalf and clear the very
    // campaign they were being asked about.
    if (isDraftDirty) return;
    // Hold, never clear, on a failed read. An id absent from a domain that errored is unknown.
    if (campaignFilterIsError) return;

    // `undefined` means the query is disabled (identity unresolved) or has never resolved. This
    // MUST precede any refetch: `refetch()` fetches a disabled query anyway, and its queryFn
    // returns [] for null identity — a "completed read" that omits everything. Resetting the
    // attempt marker here is also the identity-switch reset, since AuthContext calls
    // queryClient.clear() on sign-out/in and both domains return to undefined.
    if (campaignFilterOptions === undefined) {
      attemptedCampaignNormalizationRef.current = null;
      return;
    }
    // A background refetch is in flight — the answer is not in yet.
    if (campaignFilterIsFetching) return;

    if (
      campaignFilterOptions.some(
        (campaign) => campaign.heyreach_campaign_id === filters.campaignId
      )
    ) {
      // Present after all. Forget the attempt so a later disappearance gets its own reconciliation.
      attemptedCampaignNormalizationRef.current = null;
      return;
    }

    // The domain kind is part of the marker: the two tab families read DIFFERENT domains through
    // the same URL parameter, so an id reconciled against the threads domain says nothing about
    // the drafts domain.
    const marker = `${campaignDomainKind}:${filters.campaignId}`;

    // ONE bounded reconciliation before clearing anything. r2 removed this step because it could
    // not observe completion; r3 showed why it is required — `useDraftCampaignDomain` has a 30s
    // staleTime, no refetchInterval, and NOTHING invalidates it when a draft arrives (the realtime
    // fan-out covers conversation-threads / thread-counts / conversation-campaigns only). So a
    // campaign whose first draft just landed is legitimately absent from a settled cache, and
    // clearing on that snapshot broadens a filter the operator deliberately chose.
    if (attemptedCampaignNormalizationRef.current !== marker) {
      attemptedCampaignNormalizationRef.current = marker;
      setCampaignNormalizationPending(marker);
      void reconcileCampaignFilter().finally(() => {
        // The state flip is what re-renders and re-runs this effect against whatever data is now
        // current — not a dependency that may never tick.
        setCampaignNormalizationPending(null);
      });
      return;
    }

    // Still in flight from the branch above; wait for the .finally().
    if (campaignNormalizationPending === marker) return;

    // A completed, current read still omits it. Now it is genuinely gone.
    // Go through the SAME guarded transition the dropdown uses — writing the URL directly bypasses
    // `applyCampaignChange`, whose dirty-draft protection and accepted-campaign bookkeeping decide
    // when a campaign change may take effect.
    // REPLACE, never push. This is a normalization, not an operator choice: pushing leaves the
    // invalid id one Back away, the effect clears it again, and the operator ping-pongs between two
    // states instead of leaving the Inbox. 7dcefa9 shipped exactly this contract for invalid
    // parameters; until now the hook's public type made it impossible to ask for.
    normalizeCampaignToAll();
  }, [
    campaignFilterOptions,
    campaignFilterIsFetching,
    campaignFilterIsError,
    campaignNormalizationPending,
    campaignDomainKind,
    filters.campaignId,
    isDraftDirty,
    reconcileCampaignFilter,
  ]);

  // URL navigation (including Back/Forward) must obey the same transition as
  // the dropdown. A layout effect keeps the accepted campaign/data scope on
  // A until dirty edits are resolved and flushes a clean transition before
  // the browser can paint B with any A-owned selection state.
  useLayoutEffect(() => {
    const nextCampaignId = urlThreadFilters.campaignId;
    const pendingInternalCampaign = pendingInternalCampaignRef.current;

    if (pendingInternalCampaign !== null) {
      if (nextCampaignId === pendingInternalCampaign) {
        pendingInternalCampaignRef.current = null;
        acceptedCampaignSearchRef.current = location.search;
        if (!sameThreadFilters(filters, urlThreadFilters)) {
          setActiveThreadFilters(urlThreadFilters);
        }
      }
      return;
    }

    // DIFF round 3, F3: the dirty-edit guard has to come BEFORE the same-campaign fast path, not
    // after it. Previously any URL transition that kept the campaign but changed another filter —
    // `campaign=A,status=needs_reply` → `campaign=A,status=reply_queue` via Back/Forward — took
    // the fast path and copied the whole URL snapshot into active state without confirmation.
    // Activating Reply Queue then clears the selected thread, draft and edit mode, so an unsaved
    // edit was silently discarded. The campaign was never the thing that mattered; invalidating
    // the selected draft is, and any filter change can do that.
    if (isDraftDirty && !sameThreadFilters(filters, urlThreadFilters)) {
      const alreadyPendingSnapshot =
        pendingDiscardIntent?.kind === 'campaign' &&
        pendingDiscardIntent.campaignId === nextCampaignId &&
        pendingDiscardIntent.urlAlreadyChanged;
      if (!alreadyPendingSnapshot || !discardConfirmationOpen) {
        setPendingDiscardIntent({
          kind: 'campaign',
          campaignId: nextCampaignId,
          urlAlreadyChanged: true,
          urlSearch: location.search,
        });
        setDiscardConfirmationOpen(true);
      }
      return;
    }

    if (nextCampaignId === filters.campaignId) {
      // DIFF round 4, F3: if a URL-driven discard dialog is open and the browser has just come
      // BACK to the already-accepted entry, the transition it was asking about no longer exists.
      // Retire the intent and close the dialog, leaving the edit untouched — otherwise the visible
      // "Discard changes" button would destroy the draft for a navigation that was abandoned.
      if (
        pendingDiscardIntent?.kind === 'campaign' &&
        pendingDiscardIntent.urlAlreadyChanged &&
        location.search === acceptedCampaignSearchRef.current
      ) {
        setPendingDiscardIntent(null);
        setDiscardConfirmationOpen(false);
        return;
      }
      acceptedCampaignSearchRef.current = location.search;
      if (!sameThreadFilters(filters, urlThreadFilters)) {
        setActiveThreadFilters(urlThreadFilters);
      }
      return;
    }

    if (isDraftDirty) {
      const alreadyPending =
        pendingDiscardIntent?.kind === 'campaign' &&
        pendingDiscardIntent.campaignId === nextCampaignId &&
        pendingDiscardIntent.urlAlreadyChanged;
      if (!alreadyPending || !discardConfirmationOpen) {
        setPendingDiscardIntent({
          kind: 'campaign',
          campaignId: nextCampaignId,
          urlAlreadyChanged: true,
          urlSearch: location.search,
        });
        setDiscardConfirmationOpen(true);
      }
      return;
    }

    applyCampaignChange(nextCampaignId, false, urlThreadFilters, location.search);
  }, [
    applyCampaignChange,
    discardConfirmationOpen,
    filters,
    isDraftDirty,
    location.search,
    pendingDiscardIntent,
    urlThreadFilters,
  ]);

  // Closes the discard dialog and clears its pending intent together — the
  // one path ("Keep editing") that must NOT act on the intent. Radix's
  // controlled Dialog only calls onOpenChange for its own dismiss triggers
  // (Escape, overlay click), not when the app changes the `open` prop
  // itself, so "Keep editing" needs to call this directly rather than
  // relying on onOpenChange to clean up after it.
  const closeDiscardConfirmation = (): void => {
    if (
      pendingDiscardIntent?.kind === 'campaign' &&
      pendingDiscardIntent.urlAlreadyChanged
    ) {
      navigate(
        `${location.pathname}${acceptedCampaignSearchRef.current}${location.hash}`,
        { replace: true }
      );
    }
    setDiscardConfirmationOpen(false);
    setPendingDiscardIntent(null);
  };

  const confirmDiscardChanges = (): void => {
    const intent = pendingDiscardIntent;

    resetDraftEdits();
    setDiscardConfirmationOpen(false);
    setPendingDiscardIntent(null);

    switch (intent?.kind) {
      case 'thread':
        selectThreadNow(intent.thread, intent.index);
        break;
      case 'close':
        setMobileDetailOpen(false);
        focusDetailOpener();
        break;
      case 'navigation':
        navigate(intent.href);
        break;
      case 'status':
        applyStatusChange(intent.status);
        break;
      case 'campaign':
        if (intent.urlAlreadyChanged) {
          applyCampaignChange(
            intent.campaignId,
            false,
            urlThreadFilters,
            location.search
          );
        } else {
          applyCampaignChange(intent.campaignId);
        }
        break;
      case 'cancel':
      case undefined:
        // Discarding the edit (resetDraftEdits above) is the whole intent.
        break;
    }
  };

  const requestDeleteThreads = (threadIds: string[]): void => {
    if (
      threadIds.length === 0 ||
      !threadIds.every(isMutationTargetActionable)
    ) {
      return;
    }
    if (document.activeElement instanceof HTMLButtonElement) {
      deleteTriggerRef.current = document.activeElement;
    }
    setPendingDeleteThreadIds([...threadIds]);
    setDeleteError(null);
    setDeleteConfirmationOpen(true);
  };

  const closeDeleteConfirmation = (): void => {
    if (softDeleteMutation.isPending) return;
    setDeleteConfirmationOpen(false);
    setPendingDeleteThreadIds([]);
    setDeleteError(null);
    window.requestAnimationFrame(() => deleteTriggerRef.current?.focus());
  };

  const confirmDeleteThreads = (): void => {
    if (
      pendingDeleteThreadIds.length === 0 ||
      !pendingDeleteThreadIds.every(isMutationTargetActionable) ||
      deleteInFlightRef.current ||
      softDeleteMutation.isPending
    ) {
      return;
    }

    const threadIds = [...pendingDeleteThreadIds];
    const selectedThreadWillBeDeleted =
      !!selectedThread && threadIds.includes(selectedThread.thread_id);
    const draftWillBeDiscarded = selectedThreadWillBeDeleted && isDraftDirty;

    deleteInFlightRef.current = true;
    setDeleteError(null);
    softDeleteMutation.mutate(
      { threadIds },
      {
        onSuccess: () => {
          if (draftWillBeDiscarded) {
            resetDraftEdits();
          }
          toast({
            title:
              threadIds.length === 1
                ? 'Thread moved to trash'
                : `${threadIds.length} threads moved to trash`,
          });
          clearSelection();
          if (selectedThreadWillBeDeleted) {
            setSelectedThread(null);
            setSelectedDraft(null);
            setMobileDetailOpen(false);
          }
          setPendingDeleteThreadIds([]);
          setDeleteConfirmationOpen(false);
          deleteInFlightRef.current = false;
        },
        onError: () => {
          setDeleteError(
            'The thread could not be moved to trash. Your draft is still preserved.'
          );
          deleteInFlightRef.current = false;
        },
      }
    );
  };

  const handleSearchChange = (value: string) => {
    setLocalSearch(value);
    debouncedSearch(value);
  };

  const handleSearchClear = () => {
    setLocalSearch('');
    setFilters({ search: '' });
  };

  const handleStatusChange = (status: ThreadStatus): void => {
    // Switching to Reply Queue hides the inline draft detail entirely (see
    // the effect above) — confirm before discarding an unsaved edit, exactly
    // as any other destructive navigation away from it does.
    if (status === 'reply_queue' && isDraftDirty) {
      setPendingDiscardIntent({ kind: 'status', status });
      setDiscardConfirmationOpen(true);
      return;
    }
    applyStatusChange(status);
  };

  const removePendingReplyGateOverride = (
    decision: PendingReplyGateOverride
  ): void => {
    setPendingReplyGateOverrides((pending) =>
      pending.filter(
        (entry) => entry.source !== decision.source || entry.draftId !== decision.draftId
      )
    );
  };

  const handleReplyGateOverride = (decision: PendingReplyGateOverride): void => {
    // The gate has already resolved the thread and removed it from the actionable list. This
    // dedicated handler is intentionally bound to the blocked response's draft id instead of the
    // now-stale selection. It is the only Inbox path that sends overrideReplyGate: true.
    if (replyGateOverrideInFlightRef.current) return;

    replyGateOverrideInFlightRef.current = true;
    const onError = (error: Error): void => {
      toast({
        title: 'Reply gate override failed',
        description: safeRecoveryMessage(error),
        variant: 'destructive',
      });
    };
    const onSettled = (): void => {
      replyGateOverrideInFlightRef.current = false;
    };

    if (decision.source === 'generate-draft') {
      replyGateGenerateOverrideMutation.mutate(
        {
          draftId: decision.draftId,
          customInstructions: decision.customInstructions,
          overrideReplyGate: true,
        },
        {
          onSuccess: (data) => {
            if (!data.success || data.blocked) {
              onError(new Error(data.error || 'Tacit could not create a draft for review.'));
              return;
            }
            removePendingReplyGateOverride(decision);
            toast(describeGateOverrideSuccess());
          },
          onError,
          onSettled,
        }
      );
      return;
    }

    replyGateRegenerateOverrideMutation.mutate(
      {
        draftIds: [decision.draftId],
        customInstructions: decision.customInstructions,
        overrideReplyGate: true,
      },
      {
        onSuccess: (data) => {
          const result = data.results.find((entry) => entry.draftId === decision.draftId);
          if (!result?.success || result.blocked) {
            onError(new Error(result?.error || 'Tacit could not create a draft for review.'));
            return;
          }
          removePendingReplyGateOverride(decision);
          toast(describeGateOverrideSuccess());
        },
        onError,
        onSettled,
      }
    );
  };

  const showReplyGateBlock = (
    source: 'generate-draft' | 'regenerate',
    rule: string | undefined,
    draftId: string,
    customInstructions?: string,
    // Defer only. `undefined` means "not a defer, or not reported"; only an explicit `false` turns
    // the toast into a warning, so an older endpoint response can never fake a failure.
    followUpScheduled?: boolean
  ): void => {
    const decision: PendingReplyGateOverride = {
      source,
      rule,
      draftId,
      customInstructions,
      followUpScheduled,
    };
    setPendingReplyGateOverrides((pending) => {
      const existingIndex = pending.findIndex(
        (entry) => entry.source === source && entry.draftId === draftId
      );
      if (existingIndex === -1) return [...pending, decision];
      const next = [...pending];
      next[existingIndex] = decision;
      return next;
    });
    toast(describeGateBlock(rule, followUpScheduled));
  };

  const handleRegenerate = (draftId: string): void => {
    if (
      !selectedThreadIsActionable ||
      regenerateInFlightRef.current ||
      regenerateMutation.isPending
    ) {
      return;
    }
    regenerateInFlightRef.current = true;
    const customInstructions = regenInstruction.trim() || undefined;
    regenerateMutation.mutate(
      {
        draftIds: [draftId],
        customInstructions,
      },
      {
        onSuccess: (data) => {
          // Branch order lives in describeRegenerateResult and is pinned by a test: a blocked
          // result carries success === true, so checking success first would announce a
          // regeneration that never happened.
          const result = data.results[0];
          if (result?.blocked) {
            showReplyGateBlock(
              'regenerate',
              result.rule,
              result.draftId,
              customInstructions,
              result.followUpScheduled
            );
          } else {
            toast(describeRegenerateResult(result));
          }
          setShowRegenInput(false);
          setRegenInstruction('');
        },
        onError: (error) => {
          toast({
            title: 'Regeneration failed',
            description: safeRecoveryMessage(error),
            variant: 'destructive',
          });
        },
        onSettled: () => {
          regenerateInFlightRef.current = false;
        },
      }
    );
  };

  const handleGenerate = (draftId: string): void => {
    if (
      !selectedThreadIsActionable ||
      generateInFlightRef.current ||
      generateMutation.isPending
    ) {
      return;
    }
    generateInFlightRef.current = true;
    generateMutation.mutate(
      { draftId },
      {
        onSuccess: (data) => {
          if (!data.blocked) return;
          showReplyGateBlock('generate-draft', data.rule, data.draftId, undefined, data.followUpScheduled);
        },
        onError: (error) => {
          toast({
            title: 'Draft generation failed',
            description: safeRecoveryMessage(error),
            variant: 'destructive',
          });
        },
        onSettled: () => {
          generateInFlightRef.current = false;
        },
      }
    );
  };

  const handleSelectAlternative = (index: number): void => {
    if (!selectedDraft) return;
    const alternatives = toDraftAlternatives(selectedDraft);
    if (!alternatives[index]) return;
    setSelectedAlternativeIndex(index);
    setSuggestionAccepted(false);
  };

  const handleAcceptSuggestion = (): void => {
    if (!selectedDraft) return;
    const alternatives = toDraftAlternatives(selectedDraft);
    const selected = getSelectedAlternative(alternatives, selectedAlternativeIndex);
    if (!selected) return;
    setEditedMessage(selected.text);
    setSuggestionAccepted(true);
  };
  
  // ───────────────────────────────────────────────────────────────────────────
  // Helpers
  // ───────────────────────────────────────────────────────────────────────────
  const asRecord = (value: unknown): Record<string, unknown> | null => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    return value as Record<string, unknown>;
  };

  const getStringValue = (value: unknown): string | null => {
    if (typeof value === 'string') return value;
    return null;
  };
  const toDraftAlternatives = (draft: DraftListItem | null): DraftAlternative[] => {
    if (!draft) return [];
    if (Array.isArray(draft.alternatives)) {
      return draft.alternatives
        .map((alternative) => {
          const text = typeof alternative.text === 'string' ? alternative.text : '';
          const approach =
            typeof alternative.approach === 'string' ? alternative.approach : 'Alternative';
          const confidence =
            typeof alternative.confidence === 'number' ? alternative.confidence : 0.7;
          return { text, approach, confidence };
        })
        .filter((alternative) => alternative.text.length > 0);
    }

    const fallbackText = draft.edited_response || draft.draft_response;
    return fallbackText
      ? [
          {
            text: fallbackText,
            approach: draft.methodology_used,
            confidence: draft.confidence,
          },
        ]
      : [];
  };

  const getSelectedAlternative = (
    alternatives: DraftAlternative[],
    index: number
  ): DraftAlternative | null => {
    if (alternatives.length === 0) return null;
    return alternatives[index] || alternatives[0] || null;
  };

  const buildEditDiff = (original: string, updated: string): string | null => {
    if (original.trim() === updated.trim()) return null;
    return `BEFORE:\n${original}\n\nAFTER:\n${updated}`;
  };

  const logBrainLearning = async (params: {
    draft: DraftListItem;
    outcome: 'approved' | 'approved_with_edit' | 'rejected';
    editDiff: string | null;
  }): Promise<void> => {
    const analysisRecord = asRecord(params.draft.analysis_report);
    const leadProfileRecord = asRecord(analysisRecord?.leadProfile);
    const sentimentRecord = asRecord(analysisRecord?.sentiment);
    const strategyRecord = asRecord(params.draft.strategy);

    const methodology = getStringValue(strategyRecord?.methodology) || params.draft.methodology_used;
    const tactics = Array.isArray(strategyRecord?.tactics)
      ? strategyRecord.tactics.filter((item): item is string => typeof item === 'string')
      : [];

    const { error } = await supabase.from('os_brain_learning').insert({
      organization_id: params.draft.organization_id,
      draft_id: params.draft.id,
      lead_seniority: getStringValue(leadProfileRecord?.seniority),
      lead_industry: getStringValue(leadProfileRecord?.industry),
      detected_sentiment: getStringValue(sentimentRecord?.sentiment),
      conversation_turn: null,
      methodology_used: methodology,
      tactics_used: tactics,
      outcome: params.outcome,
      human_edit_diff: params.editDiff,
    });

    if (error) {
      // Learning loop failure should not block approval flow
      return;
    }
  };
  
  const getConstraintDots = (validation: ConstraintValidation | null) => {
    if (!validation) return null;
    return (
      <div className="flex items-center gap-1" title={`Constraints: ${validation.score}/${validation.total}`}>
        <ShieldCheck className="h-3 w-3 text-muted-foreground" />
        {validation.constraints.map((c) => (
          <span
            key={c.id}
            className={cn(
              'flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold',
              c.passed
                ? 'bg-status-success/15 text-status-success'
                : 'bg-destructive/15 text-destructive'
            )}
            title={`${c.rule}${c.detail ? `: ${c.detail}` : ''}`}
            aria-label={`${c.rule}: ${c.passed ? 'passed' : 'failed'}${
              c.detail ? `. ${c.detail}` : ''
            }`}
          >
            {c.passed ? '✓' : '×'}
          </span>
        ))}
      </div>
    );
  };

  const renderAiReasoningPanel = () => {
    if (!selectedDraft || !showAiReasoning) return null;

    const strategy = selectedDraft.strategy as Record<string, unknown> | null;
    const decisionTrace = selectedDraft.decision_trace as Record<string, unknown> | null;
    const validation = selectedDraft.constraint_validation;
    const versions = selectedDraft.draft_versions;

    return (
      <div className="mx-auto rounded-lg border bg-muted/30 p-4 text-sm">
        <div className="mb-3 flex items-center justify-between">
          <h4 className="font-semibold text-foreground">AI Reasoning</h4>
          <button
            type="button"
            onClick={() => setShowAiReasoning(false)}
            className="flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground hover:bg-surface-hover hover:text-foreground"
            aria-label="Close AI reasoning"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3">
          {/* Methodology & Confidence */}
          <div className="flex items-center gap-4">
            <div>
              <span className="text-xs text-muted-foreground">Methodology:</span>{' '}
              <Badge {...getMethodologyBadge(selectedDraft.methodology_used)} className="text-xs">
                {selectedDraft.methodology_used.toUpperCase()}
              </Badge>
            </div>
            <div>
              <span className="text-xs text-muted-foreground">Confidence:</span>{' '}
              <span className="font-medium">{Math.round(selectedDraft.confidence * 100)}%</span>
            </div>
            {selectedDraft.regeneration_count != null && selectedDraft.regeneration_count > 0 && (
              <div>
                <Badge variant="outline" className="text-xs">
                  <RefreshCw className="mr-1 h-3 w-3" />
                  v{selectedDraft.regeneration_count + 1}
                </Badge>
              </div>
            )}
          </div>

          {/* Strategy Reasoning */}
          {typeof strategy?.reasoning === 'string' ? (
            <div>
              <span className="text-xs font-medium text-muted-foreground">Strategy:</span>
              <p className="mt-1 text-xs text-foreground">{strategy.reasoning}</p>
            </div>
          ) : null}

          {/* Momentum & Funnel */}
          {decisionTrace != null && (
            <div className="flex flex-wrap gap-4">
              {decisionTrace.funnelStage != null ? (
                <div className="text-xs">
                  <span className="text-muted-foreground">Funnel:</span>{' '}
                  <span className="font-medium">{String(decisionTrace.funnelStage)}</span>
                </div>
              ) : null}
              {decisionTrace.funnelBANT != null ? (
                <div className="text-xs">
                  <span className="text-muted-foreground">BANT:</span>{' '}
                  <span className="font-medium">{String(decisionTrace.funnelBANT)}</span>
                </div>
              ) : null}
              {decisionTrace.momentum != null && typeof decisionTrace.momentum === 'object' ? (
                <div className="text-xs">
                  <span className="text-muted-foreground">Momentum:</span>{' '}
                  <span className="font-medium">
                    {String((decisionTrace.momentum as Record<string, unknown>).trend)} (
                    {Number((decisionTrace.momentum as Record<string, unknown>).score || 0).toFixed(2)})
                  </span>
                </div>
              ) : null}
            </div>
          )}

          {/* Constraint Validation */}
          {validation && (
            <div>
              <span className="text-xs font-medium text-muted-foreground">Constraints:</span>
              <div className="mt-1 flex flex-wrap gap-2">
                {validation.constraints.map((c) => (
                  <span
                    key={c.id}
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${
                      c.passed
                        ? 'bg-green-500/10 text-green-700 dark:text-green-400'
                        : 'bg-red-500/10 text-red-700 dark:text-red-400'
                    }`}
                  >
                    {c.passed ? '\u2713' : '\u2717'} {c.rule}
                    {c.detail && !c.passed && (
                      <span className="text-[10px] opacity-70">({c.detail})</span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Version History */}
          {versions && versions.length > 0 && (
            <div>
              <span className="text-xs font-medium text-muted-foreground">
                Previous versions ({versions.length}):
              </span>
              <div className="mt-1 space-y-1">
                {versions.map((v, i) => (
                  <div key={`version-${i}`} className="rounded bg-muted/50 px-2 py-1 text-xs">
                    <span className="font-medium">v{i + 1}</span>{' '}
                    <span className="text-muted-foreground">
                      {v.methodology?.toUpperCase()} ({Math.round((v.confidence || 0) * 100)}%)
                    </span>
                    {v.instruction && (
                      <span className="ml-2 italic text-muted-foreground">
                        &ldquo;{v.instruction}&rdquo;
                      </span>
                    )}
                    {/*
                      Until 2026-08-12 this was a permanent `line-clamp-2` with no expand
                      affordance anywhere, which made the version list decorative: you could
                      see that three versions existed and never read one, let alone compare
                      two.

                      Native <details> rather than the Reply Queue's ReplyQueueMessageDisclosure
                      on purpose. That component is built for ONE prominent message per row and
                      carries a full-width 44px touch-target button; this panel renders N
                      compact versions, so N such buttons would dominate it. <details> needs no
                      state, is keyboard- and screen-reader-operable for free, and each version
                      opens independently.

                      Exactly one copy of the text is exposed at a time: the clamped preview is
                      display:none'd when open (`group-open:hidden`), and the full body is
                      hidden by the browser while closed. whitespace-pre-line keeps the
                      paragraph breaks the draft was written with.
                    */}
                    <details className="group mt-0.5">
                      <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                        <span className="line-clamp-2 whitespace-pre-line text-muted-foreground group-open:hidden">
                          {v.text}
                        </span>
                        <span className="mt-0.5 block text-[10px] text-muted-foreground underline">
                          <span className="group-open:hidden">Show full version</span>
                          <span className="hidden group-open:inline">Hide full version</span>
                        </span>
                      </summary>
                      <p
                        className="mt-1 whitespace-pre-line text-muted-foreground"
                        data-testid={`draft-version-body-${i}`}
                      >
                        {v.text}
                      </p>
                    </details>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const getMethodologyBadge = (methodology: string) => {
    const config = {
      sandler: { label: 'Sandler', variant: 'default' as const },
      challenger: { label: 'Challenger', variant: 'secondary' as const },
      spin: { label: 'SPIN', variant: 'secondary' as const },
      voss_recovery: { label: 'Voss', variant: 'outline' as const },
      human_handover: { label: 'Human', variant: 'outline' as const },
      manual_fallback: { label: 'Manual', variant: 'outline' as const },
    };
    
    return config[methodology as keyof typeof config] || config.sandler;
  };
  
  const getConfidenceBadge = (confidence: number) => {
    if (confidence >= 0.85) return { label: 'High', variant: 'default' as const };
    if (confidence >= 0.70) return { label: 'Medium', variant: 'secondary' as const };
    return { label: 'Low', variant: 'outline' as const };
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, { label: string; variant: 'secondary' | 'outline' | 'default' | 'destructive' }> = {
      pending_approval: { label: 'Pending', variant: 'secondary' },
      approved: { label: 'Approved', variant: 'outline' },
      sent: { label: 'Sent', variant: 'default' },
      rejected: { label: 'Rejected', variant: 'destructive' },
      failed: { label: 'Failed', variant: 'destructive' },
      awaiting_draft: { label: 'Awaiting Draft', variant: 'outline' },
    };

    const fallback = { label: 'Unknown', variant: 'outline' as const };
    return config[status] ?? fallback;
  };

  const shortcuts = useMemo<ShortcutConfig[]>(
    () => [
      {
        key: 'j',
        handler: () =>
          setSelectedIndex((index) =>
            threads.length === 0 ? 0 : Math.min(index + 1, threads.length - 1)
          ),
        description: 'Next conversation',
      },
      {
        key: 'k',
        handler: () => setSelectedIndex((index) => Math.max(index - 1, 0)),
        description: 'Previous conversation',
      },
      {
        key: 'e',
        handler: () => selectedDraft && !isEditing && handleEdit(),
        description: 'Edit draft',
      },
      {
        key: 'tab',
        when: () => Boolean(
          selectedDraft &&
          !isEditing &&
          !suggestionAccepted
        ),
        handler: handleAcceptSuggestion,
        description: 'Accept suggestion',
      },
      {
        key: '1',
        handler: () => selectedDraft && !isEditing && handleSelectAlternative(0),
        description: 'Alternative 1',
      },
      {
        key: '2',
        handler: () => selectedDraft && !isEditing && handleSelectAlternative(1),
        description: 'Alternative 2',
      },
      {
        key: '3',
        handler: () => selectedDraft && !isEditing && handleSelectAlternative(2),
        description: 'Alternative 3',
      },
      {
        key: '?',
        shift: true,
        handler: () => setShowShortcutsHelp(true),
        description: 'Show shortcuts',
      },
      {
        key: '/',
        handler: () => searchInputRef.current?.focus(),
        description: 'Focus search',
      },
      {
        key: 'Escape',
        handler: () => {
          if (isEditing) handleCancelEdit();
          if (showShortcutsHelp) setShowShortcutsHelp(false);
        },
        description: 'Cancel',
      },
    ],
    [
      threads.length,
      selectedDraft,
      isEditing,
      suggestionAccepted,
      showShortcutsHelp,
      handleEdit,
      handleCancelEdit,
      handleAcceptSuggestion,
      handleSelectAlternative,
    ]
  );

  useKeyboardShortcuts(shortcuts, !isEditing);
  // ───────────────────────────────────────────────────────────────────────────
  // Render
  // ───────────────────────────────────────────────────────────────────────────

  // F1/F3: what the detail pane shows while nothing is fully resolved yet —
  // distinct loading/error-with-retry/not-found states for BOTH deep-link
  // resolution steps (the thread, then that thread's own drafts), never
  // silently falling through to "No draft selected" (which reads as
  // "nothing was requested" rather than "resolution isn't done yet").
  const renderEmptyDetailState = (surface: 'inline' | 'sheet'): JSX.Element => {
    const wrapperClass = surface === 'inline'
      ? 'flex h-full items-center justify-center'
      : 'flex h-full items-center justify-center p-6';

    if (deepLinkResolutionState === 'loading' || deepLinkResolutionState === 'draft-loading') {
      const isDraftStep = deepLinkResolutionState === 'draft-loading';
      return (
        <div role="status" aria-label={isDraftStep ? 'Loading targeted draft' : 'Loading targeted conversation'} className={wrapperClass}>
          <div className="text-center">
            <Skeleton className="mx-auto h-12 w-12 rounded-full" />
            <p className="mt-4 text-sm text-muted-foreground">{isDraftStep ? 'Loading draft…' : 'Loading conversation…'}</p>
          </div>
        </div>
      );
    }
    if (deepLinkResolutionState === 'error' || deepLinkResolutionState === 'draft-error') {
      const isDraftStep = deepLinkResolutionState === 'draft-error';
      const query = isDraftStep ? threadMessagesQuery : deepLinkFallbackQuery;
      return (
        <div role="alert" className={wrapperClass}>
          <div className="text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-destructive" aria-hidden="true" />
            <h2 className="mt-4 text-lg font-medium">
              {isDraftStep ? "Couldn't load this conversation's drafts" : "Couldn't load this conversation"}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">{safeRecoveryMessage(query.error)}</p>
            <Button variant="outline" className="mt-4 min-h-11 md:min-h-11" onClick={() => void query.refetch()}>
              Retry
            </Button>
          </div>
        </div>
      );
    }
    if (deepLinkResolutionState === 'not-found') {
      return (
        <div className={wrapperClass}>
          <div className="text-center">
            <LinkedinIcon className="mx-auto h-16 w-16 text-muted-foreground" aria-hidden="true" />
            <h2 className="mt-4 text-lg font-medium">Conversation not found</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              This conversation may have been removed or is not available to you.
            </p>
          </div>
        </div>
      );
    }
    if (deepLinkResolutionState === 'draft-empty') {
      return (
        <div className={wrapperClass}>
          <div className="text-center">
            <LinkedinIcon className="mx-auto h-16 w-16 text-muted-foreground" aria-hidden="true" />
            <h2 className="mt-4 text-lg font-medium">No draft found for this conversation</h2>
            <p className="mt-2 text-sm text-muted-foreground">This conversation has no recorded messages yet.</p>
          </div>
        </div>
      );
    }
    return (
      <div className={wrapperClass}>
        <div className="text-center">
          <LinkedinIcon className="mx-auto h-16 w-16 text-muted-foreground" aria-hidden="true" />
          <h2 className="mt-4 text-lg font-medium">No draft selected</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {surface === 'inline'
              ? 'Select a conversation from the list to review its draft.'
              : 'Select a conversation to view details.'}
          </p>
        </div>
      </div>
    );
  };

  const renderSidebarContent = () => {
    if (isReplyQueueTab) {
      return (
        <ReplyQueuePanel
          rows={replyQueueRowsQuery.data?.rows ?? []}
          isLoading={replyQueueRowsQuery.isLoading}
          isError={replyQueueRowsQuery.isError}
          error={replyQueueRowsQuery.error}
          archivedExcludedCount={replyQueueRowsQuery.data?.archivedExcludedCount ?? 0}
          includeArchived={includeArchivedInReplyQueue}
          onIncludeArchivedChange={setIncludeArchivedInReplyQueue}
          onRetry={() => replyQueueRowsQuery.refetch()}
          onNavigate={requestGuardedNavigation}
        />
      );
    }

    return (
    <>
      {isError ? (
        <div role="alert" className="p-8 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-destructive" />
          <h3 className="mt-4 text-lg font-medium">Failed to load conversations</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            {safeRecoveryMessage(error)}
          </p>
          <Button
            variant="outline"
            className="mt-4 min-h-11 md:min-h-11"
            onClick={() => refetch()}
          >
            Retry
          </Button>
        </div>
      ) : isLoading ? (
        <div role="status" aria-label="Loading conversations" className="space-y-3 p-4">
          <span className="sr-only">Loading conversations…</span>
          {[1, 2, 3].map((item) => (
            <Card key={item} className="p-4">
              <div className="flex items-start gap-3">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-3 w-2/3" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : filteredThreads.length === 0 && threads.length === 0 ? (
        <div role="status" aria-label="Inbox all clear" className="p-8 text-center">
          <InboxIcon className="mx-auto h-12 w-12 text-muted-foreground" />
          <h3 className="mt-4 text-lg font-medium">{emptyInboxCopy(filters.status).title}</h3>
          <p className="mt-2 text-sm text-muted-foreground">{emptyInboxCopy(filters.status).body}</p>
        </div>
      ) : filteredThreads.length === 0 ? (
        <div className="p-8 text-center">
          <InboxIcon className="mx-auto h-12 w-12 text-muted-foreground" />
          <h3 className="mt-4 text-lg font-medium">No matches</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            No conversations match the active filters.
          </p>
        </div>
      ) : (
        <>
          {/* Animated bulk actions bar */}
          <AnimatePresence>
            {selectedThreadIdsAreActionable && (
              <motion.div
                key="bulk-bar"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.15, ease: 'easeOut' }}
                className="overflow-hidden"
              >
                <BulkActionsBar
                  selectedCount={selectedThreadIds.size}
                  isDeletedTab={filters.status === 'deleted'}
                  onDelete={() => {
                    requestDeleteThreads(Array.from(selectedThreadIds));
                  }}
                  onResolve={() => {
                    const threadIds = Array.from(selectedThreadIds);
                    if (!threadIds.every((threadId) => actionableThreadIds.has(threadId))) return;
                    // Every row on the Follow-up tab is ALREADY resolved — that is what a deferral
                    // does. Writing `status: 'resolved'` over it changes nothing, reports success,
                    // and leaves the row and the badge exactly where they were. Here the same
                    // button has to clear the date, because the date is what the tab is made of.
                    if (filters.status === 'follow_up') {
                      dismissFollowUpMutation.mutate(
                        { threadIds },
                        {
                          onSuccess: ({ dismissed }) => {
                            toast({ title: `${dismissed} follow-up${dismissed === 1 ? '' : 's'} dismissed` });
                            clearSelection();
                          },
                          onError: (error) => {
                            toast({
                              title: 'Follow-up not dismissed',
                              description: safeRecoveryMessage(error),
                              variant: 'destructive',
                            });
                          },
                        }
                      );
                      return;
                    }
                    bulkResolveMutation.mutate(
                      { threadIds },
                      {
                        onSuccess: () => {
                          toast({ title: `${selectedThreadIds.size} threads resolved` });
                          clearSelection();
                        },
                      }
                    );
                  }}
                  resolveLabel={filters.status === 'follow_up' ? 'Dismiss follow-up' : undefined}
                  onRestore={() => {
                    const threadIds = Array.from(selectedThreadIds);
                    if (!threadIds.every((threadId) => actionableThreadIds.has(threadId))) return;
                    restoreMutation.mutate(
                      { threadIds },
                      {
                        onSuccess: () => {
                          toast({ title: `${selectedThreadIds.size} threads restored` });
                          clearSelection();
                        },
                      }
                    );
                  }}
                  onCancel={clearSelection}
                  isDeleting={softDeleteMutation.isPending}
                  isResolving={bulkResolveMutation.isPending || dismissFollowUpMutation.isPending}
                  isRestoring={restoreMutation.isPending}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Select all header */}
          {filteredThreads.length > 0 && (
            <div className="hidden items-center gap-2 border-b px-4 py-2 md:flex">
              <Checkbox
                checked={allPageSelected ? true : someSelected ? 'indeterminate' : false}
                onCheckedChange={handleSelectAll}
                aria-label="Select all threads"
                disabled={isPlaceholderData}
              />
              <span className="text-xs text-muted-foreground">
                {allPageSelected && !isAllAcrossPages ? (
                  <>
                    All {filteredThreads.length} on this page.{' '}
                    <button
                      type="button"
                      className="text-primary underline hover:text-primary/80"
                      onClick={() => setIsAllAcrossPages(true)}
                    >
                      Select all {threadsData?.total ?? filteredThreads.length}
                    </button>
                  </>
                ) : someSelected ? (
                  `${isAllAcrossPages ? (threadsData?.total ?? selectedThreadIds.size) : selectedThreadIds.size} selected`
                ) : (
                  'Select all'
                )}
              </span>
            </div>
          )}

          <div className="space-y-2 p-2">
            {filteredThreads.map((thread, index) => (
              <div key={thread.thread_id} className="flex min-w-0 items-start gap-2">
                <div className="hidden items-center pt-4 md:flex">
                  <span
                    id={`select-conversation-label-${thread.thread_id}`}
                    className="sr-only"
                  >
                    Select conversation with
                  </span>
                  <Checkbox
                    checked={selectedThreadIds.has(thread.thread_id)}
                    onCheckedChange={() => handleThreadSelect(thread.thread_id, index, false)}
                    disabled={!actionableThreadIds.has(thread.thread_id)}
                    aria-labelledby={`select-conversation-label-${thread.thread_id} inbox-thread-name-${thread.thread_id}`}
                    onClick={(e: React.MouseEvent) => {
                      if (e.shiftKey) {
                        e.preventDefault();
                        handleThreadSelect(thread.thread_id, index, true);
                      }
                    }}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <ThreadListItem
                    thread={thread}
                    isSelected={selectedThread?.thread_id === thread.thread_id}
                    leadNameId={`inbox-thread-name-${thread.thread_id}`}
                    showFollowUpDue={filters.status === 'follow_up'}
                    onClick={(event) => {
                      const idx = filteredThreads.findIndex(
                        (t) => t.thread_id === thread.thread_id
                      );
                      requestThreadSelection(
                        thread,
                        idx >= 0 ? idx : index,
                        event.currentTarget
                      );
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {filteredThreads.length > 0 && (
        <div className="flex items-center justify-between px-4 pb-4 pt-2 text-sm text-muted-foreground">
          <span>
            Page {filters.page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="min-h-11 min-w-11 md:min-h-11 md:min-w-11"
              onClick={() => setFilters({ page: Math.max(filters.page - 1, 1) })}
              disabled={filters.page <= 1}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="min-h-11 min-w-11 md:min-h-11 md:min-w-11"
              onClick={() => setFilters({ page: filters.page + 1 })}
              disabled={!threadsData?.hasMore}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </>
    );
  };

  const renderConversationDetail = (surface: 'inline' | 'sheet') => {
    if (!selectedDraft && !awaitingDraft) return null;
    const displayDraft = selectedDraft || awaitingDraft;
    if (!displayDraft) return null;
    const detailThread =
      threads.find((thread) => thread.thread_id === selectedThread?.thread_id) ??
      (deepLinkFallbackThread?.thread_id === selectedThread?.thread_id
        ? deepLinkFallbackThread
        : selectedThread);
    const containmentNoticeId =
      `${LINKEDIN_SEND_CONTAINMENT_NOTICE_ID}-${surface}`;
    const alternatives = selectedDraft ? toDraftAlternatives(selectedDraft) : [];
    const selectedAlternative = getSelectedAlternative(alternatives, selectedAlternativeIndex);
    const suggestionText = selectedAlternative?.text || editedMessage;
    const counterLength = suggestionAccepted || isEditing ? editedMessage.length : suggestionText.length;
    const draftContentOrigin =
      suggestionAccepted &&
      selectedDraft?.edited_response != null &&
      editedMessage === selectedDraft.edited_response
        ? 'user'
        : 'generated';

    return (
      <>
        <div className="border-b bg-card p-4 pr-14">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-foreground">
                <LinkedinIcon className="h-6 w-6" />
              </div>
              <div>
                <h2
                  className="text-xl font-semibold"
                  data-testid="contact-name"
                  data-content-origin="external"
                >
                  {displayDraft.lead_name}
                </h2>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Briefcase className="h-3 w-3" />
                  <span data-content-origin="external">
                    {displayDraft.lead_title}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Building2 className="h-3 w-3" />
                  <span data-content-origin="external">
                    {displayDraft.lead_company}
                  </span>
                </div>
                {'sender' in displayDraft && displayDraft.sender?.display_name && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    Seat:{' '}
                    <span data-content-origin="domain">
                      {displayDraft.sender.display_name}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="shrink-0 text-right">
              <div className="flex items-center justify-end gap-2 text-xs text-muted-foreground">
                {detailThread?.campaign_name && (
                  <Badge
                    variant="secondary"
                    data-testid="thread-detail-campaign-badge"
                    data-content-origin="domain"
                  >
                    {detailThread.campaign_name}
                  </Badge>
                )}
                <Badge variant={getStatusBadge(displayDraft.status).variant}>
                  {getStatusBadge(displayDraft.status).label}
                </Badge>
                <span>Waiting {formatUiWaitingDuration(displayDraft.created_at)}</span>
              </div>
              <div className="flex flex-col items-end gap-1">
                {displayDraft.lead_linkedin_url && (
                  <a
                    href={displayDraft.lead_linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-h-11 items-center text-xs text-primary hover:underline"
                  >
                    View LinkedIn Profile
                    <span className="sr-only"> (opens in a new tab)</span> →
                  </a>
                )}
                <Link
                  to={leadDossierHref(displayDraft.heyreach_lead_id)}
                  className="inline-flex min-h-11 items-center text-xs text-muted-foreground hover:text-primary hover:underline"
                >
                  Full Dossier →
                </Link>
                {selectedThreadIsActionable && selectedThread && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-1 min-h-11 gap-1 text-xs text-muted-foreground hover:text-destructive md:min-h-11"
                    onClick={(event) => {
                      deleteTriggerRef.current = event.currentTarget;
                      requestDeleteThreads([selectedThread.thread_id]);
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                    Delete
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        <ScrollArea
          className="min-h-0 flex-1"
          data-scrollport="inbox-detail"
          aria-label="Conversation detail"
        >
          <div className="space-y-4 p-4 sm:p-6">
            {displayDraft.analysis_report && (
              <CommandCenter
                analysis={displayDraft.analysis_report}
                strategy={displayDraft.strategy}
                decisionTrace={displayDraft.decision_trace}
              />
            )}

            {/* codex-review .contract-lens r12 F1: rendered ONCE, above BOTH branches below, so a
                single-message thread (which never mounts ThreadTimeline) still surfaces a
                captured_only/not_connected integration state instead of silently showing an
                unexplained single message with no history. */}
            <IntegrationStateAlert integrationState={conversationHistory?.integration} />

            {/* Thread Timeline - shows full conversation history */}
            {threadMessages && threadMessages.length > 1 ? (
              <ThreadTimeline
                messages={threadMessages}
                initialOutreach={selectedThread?.initial_outreach_message || null}
                conversationMessages={conversationHistory?.messages}
                isLoadingHistory={historyLoading}
              />
            ) : (
              <div className="flex justify-start">
                <div className="max-w-[80%] rounded-lg bg-muted p-4">
                  <div
                    className="mb-1 text-xs font-medium text-muted-foreground"
                    data-content-origin="external"
                  >
                    {displayDraft.lead_name}
                  </div>
                  <p className="text-sm" data-content-origin="external">
                    {displayDraft.incoming_message}
                  </p>
                  <div className="mt-2 text-xs text-muted-foreground">
                    {formatUiDateTime(displayDraft.incoming_at, {
                      hour: '2-digit',
                      minute: '2-digit',
                      day: 'numeric',
                      month: 'short',
                    })}
                  </div>
                </div>
              </div>
            )}

            {activeDraft && selectedDraft && (
            <div className="flex justify-end">
              <div className="max-w-[80%] rounded-lg bg-primary p-4 text-primary-foreground">
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-xs font-medium opacity-90">Your draft reply</span>
                  <div className="flex gap-1">
                    {selectedDraft.methodology_used && (
                    <Badge
                      {...getMethodologyBadge(selectedDraft.methodology_used)}
                      className="text-xs"
                      data-testid="methodology-badge"
                    >
                      {selectedDraft.methodology_used.toUpperCase()}
                    </Badge>
                    )}
                    <Badge
                      {...getConfidenceBadge(selectedDraft.confidence)}
                      className="text-xs"
                      data-testid="confidence-score"
                    >
                      {Math.round(selectedDraft.confidence * 100)}%
                    </Badge>
                  </div>
                </div>

                <div className="space-y-2">
                  <GhostTextEditor
                    value={editedMessage}
                    suggestion={suggestionText}
                    isAccepted={suggestionAccepted}
                    isEditing={isEditing}
                    contentOrigin={draftContentOrigin}
                    onChange={(value) => setEditedMessage(value)}
                  />
                  <div className="flex items-center justify-between text-xs opacity-90">
                    <span>{counterLength}/300 characters</span>
                    {!suggestionAccepted && !isEditing && (
                      <span className="text-status-warning">
                        Press Tab to accept suggestion
                      </span>
                    )}
                    {editedMessage.length > 250 && (
                      <span className="text-status-warning">
                        <AlertCircle className="inline h-3 w-3" /> Near limit
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* On-demand draft generation — shown when message received but no AI draft yet */}
            {selectedThreadIsActionable && !activeDraft && awaitingDraft && (
              <div className="flex justify-end">
                <Card className="max-w-[80%] border-2 border-dashed border-primary/30 p-4">
                  <p className="mb-3 text-sm text-muted-foreground">
                    New message received. Generate an AI draft response.
                  </p>
                  <Button
                    onClick={() => handleGenerate(awaitingDraft.id)}
                    disabled={generateMutation.isPending}
                    className="gap-2"
                    aria-busy={generateMutation.isPending}
                  >
                    {generateMutation.isPending ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4" />
                    )}
                    {generateMutation.isPending ? 'Generating...' : 'Generate Draft'}
                  </Button>
                </Card>
              </div>
            )}

            {activeDraft && (
            <AlternativesPicker
              alternatives={alternatives}
              selectedIndex={selectedAlternativeIndex}
              onSelect={handleSelectAlternative}
            />
            )}

            {/* AI Reasoning Toggle + Constraint Dots */}
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setShowAiReasoning(!showAiReasoning)}
                className="flex min-h-11 items-center gap-2 rounded-md px-2 text-xs text-muted-foreground hover:bg-surface-hover hover:text-foreground"
                aria-expanded={showAiReasoning}
              >
                <Sparkles className="h-3 w-3" />
                {showAiReasoning ? 'Hide' : 'Show'} AI Reasoning
                {showAiReasoning ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </button>
              {selectedDraft && getConstraintDots(selectedDraft.constraint_validation)}
            </div>

            {/* AI Reasoning Panel (collapsible) */}
            {renderAiReasoningPanel()}
          </div>
        </ScrollArea>

        {selectedThreadIsActionable && (
          <div
            className="shrink-0 border-t bg-card p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:p-4"
            data-testid="draft-action-footer"
          >
          {actionError && (
            <div
              role="alert"
              className="mx-auto mb-3 flex max-w-3xl flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-3"
            >
              <div>
                <p className="text-sm font-medium">Message was not sent</p>
                <p className="text-xs text-muted-foreground">{actionError}</p>
              </div>
              {!isLinkedInSendContained() && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleApprove}
                  disabled={approveMutation.isPending}
                >
                  Retry send
                </Button>
              )}
            </div>
          )}
          {/* Awaiting draft — show generate prompt in action bar */}
          {!activeDraft && awaitingDraft && (
            <div className="mx-auto flex max-w-3xl items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                Click Generate to create an AI-powered draft response
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    if (!selectedThreadIsActionable || !selectedThread) return;
                    // Same reason as the bulk bar: a Follow-up row is already `resolved`, so
                    // resolving it again is a no-op with a success toast. Clear the date instead.
                    if (filters.status === 'follow_up') {
                      dismissFollowUpMutation.mutate(
                        { threadIds: [selectedThread.thread_id] },
                        {
                          onSuccess: () => toast({ title: 'Follow-up dismissed' }),
                          onError: (error) =>
                            toast({
                              title: 'Follow-up not dismissed',
                              description: safeRecoveryMessage(error),
                              variant: 'destructive',
                            }),
                        }
                      );
                      return;
                    }
                    supabase
                      .from('os_conversation_threads')
                      .update({ status: 'resolved' })
                      .eq('id', selectedThread.thread_id)
                      .then(() => {
                        queryClient.invalidateQueries({ queryKey: ['conversation-threads'] });
                        queryClient.invalidateQueries({ queryKey: ['thread-counts'] });
                        toast({ title: 'Thread resolved' });
                      });
                  }}
                  className="gap-2"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  {filters.status === 'follow_up' ? 'Dismiss follow-up' : 'Resolve'}
                </Button>
                <Button
                  onClick={() => handleGenerate(awaitingDraft.id)}
                  disabled={generateMutation.isPending}
                  className="gap-2"
                  aria-busy={generateMutation.isPending}
                >
                  {generateMutation.isPending ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                  {generateMutation.isPending ? 'Generating...' : 'Generate Draft'}
                </Button>
              </div>
            </div>
          )}
          {/* Regeneration instruction input (shown when Regenerate clicked) */}
          {showRegenInput && selectedDraft && (
            <div className="mx-auto mb-3 flex max-w-3xl items-center gap-2">
              <Input
                aria-label="Regeneration instructions"
                placeholder='Guide the AI: "be more direct", "try Challenger", "mention their funding"'
                value={regenInstruction}
                onChange={(e) => setRegenInstruction(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRegenerate(selectedDraft.id);
                  if (e.key === 'Escape') {
                    setShowRegenInput(false);
                    setRegenInstruction('');
                  }
                }}
                className="min-h-11 flex-1 text-sm md:min-h-11"
                autoFocus
              />
              <Button
                size="sm"
                onClick={() => handleRegenerate(selectedDraft.id)}
                disabled={regenerateMutation.isPending}
                className="min-h-11 gap-1 md:min-h-11"
              >
                {regenerateMutation.isPending ? (
                  <RefreshCw className="h-3 w-3 animate-spin" />
                ) : (
                  <Sparkles className="h-3 w-3" />
                )}
                Go
              </Button>
              <Button
                size="sm"
                variant="ghost"
                aria-label="Cancel regeneration"
                className="h-11 w-11 p-0"
                onClick={() => {
                  setShowRegenInput(false);
                  setRegenInstruction('');
                }}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}

          <div className="mx-auto flex max-w-3xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div
              id={
                isLinkedInSendContained()
                  ? containmentNoticeId
                  : undefined
              }
              className="flex items-center gap-2 text-sm text-muted-foreground"
            >
              {isLinkedInSendContained()
                ? LINKEDIN_SEND_CONTAINMENT_MESSAGE
                : isEditing
                  ? 'Editing draft...'
                  : 'Review and approve to send via HeyReach'}
              {isDraftDirty && (
                <Badge variant="outline" className="border-status-warning/50 text-status-warning">
                  Unsaved changes
                </Badge>
              )}
              {selectedDraft?.regeneration_count != null && selectedDraft.regeneration_count > 0 && (
                <Badge variant="outline" className="text-xs">
                  <RefreshCw className="mr-1 h-3 w-3" />
                  v{selectedDraft.regeneration_count + 1}
                </Badge>
              )}
            </div>

            <div className="flex w-full flex-wrap justify-end gap-2 sm:w-auto">
              {isEditing ? (
                <>
                  <Button
                    variant="outline"
                    onClick={handleCancelEdit}
                    disabled={approveMutation.isPending}
                    className="h-11 flex-none"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleApprove}
                    disabled={isLinkedInSendContained() || approveMutation.isPending}
                    className="h-11 min-w-0 flex-1 gap-2 sm:flex-none"
                    data-testid="approve-draft-button"
                    aria-busy={approveMutation.isPending}
                    aria-describedby={
                      isLinkedInSendContained()
                        ? containmentNoticeId
                        : undefined
                    }
                  >
                    <Send className="h-4 w-4" />
                    {approveMutation.isPending ? 'Sending…' : 'Approve & Send'}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={handleEdit}
                    disabled={approveMutation.isPending}
                    className="h-11 flex-none gap-2"
                    data-testid="edit-draft-button"
                  >
                    <Edit3 className="h-4 w-4" />
                    Edit
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="h-11 w-11 p-0"
                        aria-label="More draft actions"
                      >
                        <MoreHorizontal className="h-5 w-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onSelect={() => setShowRegenInput(true)}
                        disabled={
                          regenerateMutation.isPending ||
                          approveMutation.isPending ||
                          (selectedDraft?.status !== 'pending_approval' &&
                            selectedDraft?.status !== 'rejected')
                        }
                        data-testid="regenerate-draft-button"
                      >
                        <RefreshCw
                          className={cn(
                            'h-4 w-4',
                            regenerateMutation.isPending && 'animate-spin'
                          )}
                        />
                        {regenerateMutation.isPending ? 'Regenerating…' : 'Regenerate'}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onSelect={handleReject}
                        disabled={
                          rejectMutation.isPending ||
                          approveMutation.isPending ||
                          regenerateMutation.isPending
                        }
                        className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                        data-testid="reject-draft-button"
                      >
                        <XCircle className="h-4 w-4" />
                        Reject
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button
                    onClick={handleApprove}
                    disabled={isLinkedInSendContained() || approveMutation.isPending}
                    className="h-11 min-w-0 flex-1 gap-2 sm:flex-none"
                    data-testid="approve-draft-button"
                    aria-busy={approveMutation.isPending}
                    aria-describedby={
                      isLinkedInSendContained()
                        ? containmentNoticeId
                        : undefined
                    }
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    {approveMutation.isPending ? 'Sending…' : 'Approve & Send'}
                  </Button>
                </>
              )}
            </div>
          </div>
          </div>
        )}
      </>
    );
  };

  const totalPages = threadsData?.total
    ? Math.ceil(threadsData.total / filters.pageSize)
    : 1;

  const renderConversationListPane = (): JSX.Element => (
    <div
      className="inbox-list-pane h-full min-h-0 min-w-0 bg-surface"
      data-testid="conversation-sidebar"
    >
      <ScrollArea
        className="h-full min-w-0"
        data-scrollport="inbox-list"
        aria-label="Conversation list"
      >
        {renderSidebarContent()}
      </ScrollArea>
    </div>
  );

  const renderReadingPane = (): JSX.Element => (
    <div
      className="inbox-inline-detail h-full min-h-0 min-w-0 overflow-hidden"
      data-testid="inline-draft-detail"
    >
      {showDraftDetail ? (
        <div className="flex h-full min-h-0 flex-col" data-testid="draft-detail-panel">
          {renderConversationDetail('inline')}
        </div>
      ) : (
        renderEmptyDetailState('inline')
      )}
    </div>
  );

  return (
    <MotionConfig reducedMotion="user">
    <Layout scrollMode="workspace">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="sr-only" role="status" aria-live="polite">
          {actionStatus}
        </div>
        <div className="border-b bg-card p-3 sm:p-4">
          <div className="flex items-start justify-between gap-3 md:items-center">
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold tracking-tight">LinkedIn Inbox</h1>
              <p className="text-sm leading-5 text-muted-foreground">
                Approve AI-generated drafts before sending
              </p>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowShortcutsHelp(true)}
                className="min-h-11 gap-2 px-2 sm:px-3 md:min-h-11"
                aria-label="Keyboard shortcuts"
              >
                <Keyboard className="h-4 w-4" />
                <span className="hidden sm:inline">Shortcuts</span>
              </Button>
            </div>
          </div>

          <div className="mt-3 flex flex-col gap-3 md:mt-4 md:flex-row md:items-center md:justify-between md:gap-4">
            <div className="w-full overflow-x-auto md:w-auto">
              <div
                role="group"
                aria-label="Conversation filters"
                className="inline-flex h-[52px] w-max min-w-full items-center justify-start rounded-lg bg-muted p-1 text-muted-foreground md:min-w-0"
              >
                <button
                  type="button"
                  aria-pressed={filters.status === 'needs_reply'}
                  onClick={() => handleStatusChange('needs_reply')}
                  className={cn(
                    'inline-flex min-h-11 min-w-11 items-center justify-center gap-2 whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:min-h-11 md:min-w-11',
                    filters.status === 'needs_reply' &&
                      'bg-background text-foreground shadow-elevation-1'
                  )}
                >
                  Needs Reply
                  {threadCountsLoading ? (
                    <Skeleton className="h-5 w-6 rounded-full" />
                  ) : (
                    <Badge variant="secondary">{threadCounts?.needs_reply || 0}</Badge>
                  )}
                </button>
                <button
                  type="button"
                  aria-label={`Follow-up: conversations the reply gate deferred whose date has arrived${
                    threadCountsLoading ? '' : `, ${threadCounts?.follow_up ?? 0} due`
                  }`}
                  aria-pressed={filters.status === 'follow_up'}
                  onClick={() => handleStatusChange('follow_up')}
                  className={cn(
                    'inline-flex min-h-11 min-w-11 items-center justify-center gap-2 whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:min-h-11 md:min-w-11',
                    filters.status === 'follow_up' &&
                      'bg-background text-foreground shadow-elevation-1'
                  )}
                >
                  Follow-up
                  {threadCountsLoading ? (
                    <Skeleton className="h-5 w-6 rounded-full" />
                  ) : (
                    <Badge variant={(threadCounts?.follow_up ?? 0) > 0 ? 'secondary' : 'outline'}>
                      {threadCounts?.follow_up || 0}
                    </Badge>
                  )}
                </button>
                <button
                  type="button"
                  aria-pressed={filters.status === 'waiting'}
                  onClick={() => handleStatusChange('waiting')}
                  className={cn(
                    'inline-flex min-h-11 min-w-11 items-center justify-center gap-2 whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:min-h-11 md:min-w-11',
                    filters.status === 'waiting' &&
                      'bg-background text-foreground shadow-elevation-1'
                  )}
                >
                  Waiting
                  {!threadCountsLoading && <Badge variant="outline">{threadCounts?.waiting || 0}</Badge>}
                </button>
                <button
                  type="button"
                  aria-pressed={filters.status === 'resolved'}
                  onClick={() => handleStatusChange('resolved')}
                  className={cn(
                    'inline-flex min-h-11 min-w-11 items-center justify-center gap-2 whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:min-h-11 md:min-w-11',
                    filters.status === 'resolved' &&
                      'bg-background text-foreground shadow-elevation-1'
                  )}
                >
                  Resolved
                  {!threadCountsLoading && <Badge variant="outline">{threadCounts?.resolved || 0}</Badge>}
                </button>
                <span
                  role="separator"
                  aria-orientation="vertical"
                  className="mx-1 h-7 w-px shrink-0 bg-border"
                />
                <button
                  type="button"
                  aria-label={`Replied: conversations where we have replied at least once, by any channel${
                    threadCountsLoading ? '' : `, ${threadCounts?.sent ?? 0} conversations`
                  }`}
                  aria-pressed={filters.status === 'sent'}
                  title="Conversations where we have replied at least once, by any channel"
                  onClick={() => handleStatusChange('sent')}
                  className={cn(
                    'inline-flex min-h-11 min-w-11 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-border bg-background/60 px-3 py-1 text-sm font-medium text-foreground ring-offset-background transition-all hover:border-primary/50 hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:min-h-11 md:min-w-11',
                    filters.status === 'sent' &&
                      'border-primary bg-primary/15 shadow-elevation-1'
                  )}
                >
                  Replied
                  {!threadCountsLoading && <Badge variant="outline">{threadCounts?.sent || 0}</Badge>}
                </button>
                <button
                  type="button"
                  aria-pressed={filters.status === 'reply_queue'}
                  onClick={() => handleStatusChange('reply_queue')}
                  className={cn(
                    'inline-flex min-h-11 min-w-11 items-center justify-center gap-2 whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:min-h-11 md:min-w-11',
                    filters.status === 'reply_queue' &&
                      'bg-background text-foreground shadow-elevation-1'
                  )}
                >
                  Reply Queue
                  {replyQueueBadgeIsLoading ? (
                    <Skeleton className="h-5 w-6 rounded-full" />
                  ) : replyQueueBadgeIsError || replyQueueBadgeCount === undefined ? (
                    <Badge
                      variant="destructive"
                      className="gap-1"
                      aria-label="Reply queue count unavailable"
                    >
                      <AlertCircle className="h-3 w-3" aria-hidden="true" />
                      <span aria-hidden="true">—</span>
                    </Badge>
                  ) : (
                    <Badge variant="secondary">{replyQueueBadgeCount}</Badge>
                  )}
                </button>
                <button
                  type="button"
                  aria-pressed={filters.status === 'deleted'}
                  onClick={() => handleStatusChange('deleted')}
                  className={cn(
                    'inline-flex min-h-11 min-w-11 items-center justify-center gap-2 whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:min-h-11 md:min-w-11',
                    filters.status === 'deleted' &&
                      'bg-background text-foreground shadow-elevation-1'
                  )}
                >
                  <Trash2 className="h-3 w-3" />
                  Trash
                  {!threadCountsLoading && (threadCounts?.deleted || 0) > 0 && (
                    <Badge variant="outline">{threadCounts?.deleted}</Badge>
                  )}
                </button>
                <button
                  type="button"
                  aria-pressed={filters.status === 'all'}
                  onClick={() => handleStatusChange('all')}
                  className={cn(
                    'inline-flex min-h-11 min-w-11 items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:min-h-11 md:min-w-11',
                    filters.status === 'all' &&
                      'bg-background text-foreground shadow-elevation-1'
                  )}
                >
                  All
                </button>
              </div>
            </div>

            <div className="flex flex-1 flex-wrap gap-2 md:flex-nowrap md:items-center md:justify-end">
              {/* Search doesn't filter Reply Queue rows — hide it there rather than
                  show a control that looks active but does nothing. State (localSearch/
                  filters.search) is preserved for when the user returns to another tab. */}
              {!isReplyQueueTab && (
                <div className="relative w-full md:max-w-sm md:flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    ref={searchInputRef}
                    aria-label="Search conversations"
                    placeholder="Search leads, companies..."
                    value={localSearch}
                    onChange={(event) => handleSearchChange(event.target.value)}
                    className="min-h-11 pl-9 pr-12 md:min-h-11"
                  />
                  {localSearch.length > 0 && (
                    <button
                      type="button"
                      onClick={handleSearchClear}
                      className="absolute right-0 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-surface-hover hover:text-foreground"
                      aria-label="Clear search"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}

              <div className="flex w-full gap-2 overflow-x-auto pb-0.5 md:w-auto md:shrink-0 md:overflow-visible md:pb-0">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={sendersLoading}
                    className="min-h-11 shrink-0 gap-2 md:min-h-11"
                  >
                    <User className="h-4 w-4" />
                    <span className="truncate">
                      {filters.senderId === 'all'
                        ? 'All Seats'
                        : filters.senderId === 'unassigned'
                        ? 'Unassigned'
                        : senders?.find((sender) => sender.id === filters.senderId)?.display_name ||
                          'Seat'}
                    </span>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={() => setFilters({ senderId: 'all' })}>
                    All Seats
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setFilters({ senderId: 'unassigned' })}>
                    Unassigned (Legacy)
                  </DropdownMenuItem>
                  {senders && senders.length > 0 && (
                    <>
                      <DropdownMenuSeparator />
                      {senders.map((sender) => (
                        <DropdownMenuItem
                          key={sender.id}
                          onClick={() => setFilters({ senderId: sender.id })}
                          className="flex items-center justify-between gap-2"
                        >
                          <span className="truncate">{sender.display_name}</span>
                          {sender.pending_count > 0 && (
                            <Badge variant="secondary">{sender.pending_count}</Badge>
                          )}
                        </DropdownMenuItem>
                      ))}
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {campaignFilterIsError ? (
                  <div
                    role="alert"
                    className="flex min-h-11 shrink-0 items-center gap-2 rounded-md border border-destructive/40 px-2 text-destructive"
                  >
                    <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                    <span className="max-w-48">
                      <span className="block text-xs font-medium">
                        Campaign filter unavailable
                      </span>
                      <span className="block truncate text-[10px] text-muted-foreground">
                        {safeRecoveryMessage(campaignFilterError)}
                      </span>
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="min-h-11 shrink-0 px-2 md:min-h-11"
                      aria-label="Retry campaign filter"
                      onClick={refetchCampaignFilter}
                    >
                      Retry
                    </Button>
                  </div>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant={filters.campaignId !== 'all' ? 'secondary' : 'outline'}
                        size="sm"
                        disabled={campaignFilterLoading || campaignFilterOptions === undefined}
                        className="min-h-11 shrink-0 gap-2 md:min-h-11"
                        aria-label="Filter conversations by campaign"
                        aria-describedby={campaignFilterDescriptionId}
                      >
                        <Briefcase className="h-4 w-4" />
                        {filters.campaignId === 'all' ? (
                          <span id={campaignFilterDescriptionId} className="max-w-44 truncate">
                            All Campaigns
                          </span>
                        ) : (
                          <span
                            id={campaignFilterDescriptionId}
                            className="max-w-44 truncate"
                            data-content-origin="domain"
                          >
                            {campaignFilterOptions?.find(
                              (campaign) =>
                                campaign.heyreach_campaign_id === filters.campaignId
                            )?.campaign_name}
                          </span>
                        )}
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-64">
                      <DropdownMenuLabel>Campaign</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuRadioGroup
                        value={filters.campaignId}
                        onValueChange={handleCampaignChange}
                      >
                        <DropdownMenuRadioItem
                          value="all"
                          className="min-h-11 md:min-h-11"
                        >
                          All Campaigns
                        </DropdownMenuRadioItem>
                        {campaignFilterOptions?.map((campaign) => {
                          if (!campaign.heyreach_campaign_id || !campaign.campaign_name) return null;
                          return (
                            <DropdownMenuRadioItem
                              key={campaign.heyreach_campaign_id}
                              value={campaign.heyreach_campaign_id}
                              className="min-h-11 md:min-h-11"
                            >
                              <span className="truncate" data-content-origin="domain">
                                {campaign.campaign_name}
                              </span>
                            </DropdownMenuRadioItem>
                          );
                        })}
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

              {/* R9 B4.6: source filter — minimal control; real utility post-cutover.
                  Reply Queue doesn't read draftFilters.source either — hide it there
                  too (URL state untouched, so it's restored on return). */}
              {!isReplyQueueTab && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant={draftFilters.source !== 'all' ? 'secondary' : 'outline'}
                    size="sm"
                    className="min-h-11 shrink-0 gap-2 md:min-h-11"
                  >
                    <ShieldCheck className="h-4 w-4" />
                    <span>
                      {draftFilters.source === 'product'
                        ? 'Product Only'
                        : draftFilters.source === 'other'
                        ? 'Other Sources'
                        : 'All Sources'}
                    </span>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  <DropdownMenuLabel>Draft Source</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {(
                    [
                      ['all', 'All Sources'],
                      ['product', 'Product Only'],
                      ['other', 'Other Sources'],
                    ] as [DraftSourceFilter, string][]
                  ).map(([value, label]) => (
                    <DropdownMenuItem
                      key={value}
                      onClick={() => setDraftFilters({ source: value })}
                      className={cn(
                        'gap-2',
                        draftFilters.source === value && 'font-medium text-primary'
                      )}
                    >
                      {label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              )}

              {/* Stage only filters `threads`, which Reply Queue doesn't use — hide
                  it there rather than show an active-looking filter with no effect.
                  stageFilter state itself is preserved for other tabs. */}
              {!isReplyQueueTab && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="min-h-11 shrink-0 gap-2 md:min-h-11"
                  >
                    <Filter className="h-4 w-4" />
                    Filters
                    {stageFilter.length > 0 && (
                      <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-[10px]">
                        {stageFilter.length}
                      </Badge>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Stage</DropdownMenuLabel>
                  {(
                    [
                      ['initial_reply', 'New Reply'],
                      ['discovery', 'Discovery'],
                      ['qualifying', 'Qualifying'],
                      ['objection_handling', 'Objection'],
                      ['next_step', 'Next Step'],
                      ['meeting_booked', 'Meeting'],
                      ['graceful_exit', 'Closing'],
                    ] as [string, string][]
                  ).map(([value, label]) => (
                    <DropdownMenuItem
                      key={value}
                      onSelect={(e) => {
                        e.preventDefault();
                        setStageFilter((prev) =>
                          prev.includes(value)
                            ? prev.filter((s) => s !== value)
                            : [...prev, value]
                        );
                      }}
                      className={cn(
                        'gap-2',
                        stageFilter.includes(value) && 'font-medium text-primary'
                      )}
                    >
                      {stageFilter.includes(value) ? (
                        <span className="h-4 w-4 text-center text-xs leading-4">✓</span>
                      ) : (
                        <span className="h-4 w-4" />
                      )}
                      {label}
                    </DropdownMenuItem>
                  ))}
                  {stageFilter.length > 0 && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onSelect={() => setStageFilter([])}
                        className="text-destructive"
                      >
                        Clear all filters
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              )}
              </div>
            </div>
          </div>

          {!isReplyQueueTab && stageFilter.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {stageFilter.map((s) => (
                <Badge key={s} variant="secondary" className="gap-1 text-xs">
                  {s.replace(/_/g, ' ')}
                  <button
                    type="button"
                    aria-label={`Remove ${s} filter`}
                    onClick={() => setStageFilter((prev) => prev.filter((x) => x !== s))}
                  >
                    <X className="h-3 w-3 cursor-pointer" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground sm:mt-4 sm:text-sm">
            <div>
              Pending:{' '}
              <span className="font-semibold text-foreground">
                {threadCountsLoading ? '…' : (threadCounts?.pending ?? '—')}
              </span>
            </div>
            {stats && (
              <>
                <div>
                  Sent Today:{' '}
                  <span className="font-semibold text-foreground">{stats.sent_today || 0}</span>
                </div>
                <div>
                  Avg Approval:{' '}
                  <span className="font-semibold text-foreground">
                    {stats.avg_approval_time_minutes
                      ? `${Math.round(stats.avg_approval_time_minutes)} min`
                      : 'N/A'}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        <div
          ref={workspaceRef}
          className="inbox-workspace flex min-h-0 flex-1 overflow-hidden"
        >
          {workspaceIsWide ? (
            <ResizablePanelGroup
              key={inboxLayoutId}
              id={inboxLayoutId}
              orientation="horizontal"
              defaultLayout={defaultLayout ?? INBOX_DEFAULT_LAYOUT}
              onLayoutChanged={onLayoutChanged}
              resizeTargetMinimumSize={INBOX_RESIZE_TARGET_SIZE}
              className="inbox-resizable-group min-h-0 min-w-0"
            >
              <ResizablePanel
                id={INBOX_LIST_PANEL_ID}
                minSize="20rem"
                className="min-h-0 min-w-0"
                data-inbox-panel="list"
              >
                {renderConversationListPane()}
              </ResizablePanel>
              <ResizableHandle
                id="inbox-pane-resize-handle"
                className="inbox-resize-handle"
                aria-label="Resize conversation list and reading pane"
                withHandle
              />
              <ResizablePanel
                id={INBOX_READING_PANEL_ID}
                minSize="20rem"
                className="min-h-0 min-w-0"
                data-inbox-panel="reading"
              >
                {renderReadingPane()}
              </ResizablePanel>
            </ResizablePanelGroup>
          ) : (
            <>
              {renderConversationListPane()}
              {renderReadingPane()}
            </>
          )}
        </div>

        <Sheet
          open={mobileDetailOpen}
          onOpenChange={(open) => {
            if (open) {
              setMobileDetailOpen(true);
            } else {
              requestDetailClose();
            }
          }}
        >
          <SheetContent
            side="bottom"
            className="inset-0 flex h-dvh w-full max-w-none flex-col p-0 md:inset-x-auto md:inset-y-0 md:left-auto md:right-0 md:h-full md:w-[min(560px,100vw)] md:max-w-[560px] md:border-l md:border-t-0"
            data-testid="draft-detail-sheet"
          >
            <SheetHeader className="sr-only">
              <SheetTitle>
                {showDraftDetail && selectedDraft
                  ? `Draft review for ${selectedDraft.lead_name}`
                  : 'Draft review'}
              </SheetTitle>
              <SheetDescription>
                Review the conversation and explicitly approve, edit, regenerate, or reject
                the draft.
              </SheetDescription>
            </SheetHeader>
            {showDraftDetail ? (
              <div className="flex h-full min-h-0 flex-col">
                {renderConversationDetail('sheet')}
              </div>
            ) : (
              renderEmptyDetailState('sheet')
            )}
          </SheetContent>
        </Sheet>

        <Dialog
          open={pendingReplyGateOverride !== null}
          onOpenChange={(open) => {
            if (open || replyGateOverrideInFlightRef.current || !pendingReplyGateOverride) return;
            removePendingReplyGateOverride(pendingReplyGateOverride);
          }}
        >
          {pendingReplyGateOverride && pendingReplyGateOverrideCopy && (
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{pendingReplyGateOverrideCopy.title}</DialogTitle>
                <DialogDescription>
                  {pendingReplyGateOverrideCopy.description}
                </DialogDescription>
              </DialogHeader>
              {pendingReplyGateOverrides.length > 1 && (
                <p className="text-sm text-muted-foreground">
                  {pendingReplyGateOverrides.length - 1} more blocked{' '}
                  {pendingReplyGateOverrides.length === 2 ? 'draft is' : 'drafts are'} waiting for
                  your decision.
                </p>
              )}
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  className="min-h-11 md:min-h-11"
                  onClick={() => removePendingReplyGateOverride(pendingReplyGateOverride)}
                  disabled={replyGateOverrideIsPending}
                >
                  Keep closed
                </Button>
                <Button
                  type="button"
                  className="min-h-11 md:min-h-11"
                  aria-label={REPLY_GATE_OVERRIDE_ACTION_ALT_TEXT}
                  onClick={() => handleReplyGateOverride(pendingReplyGateOverride)}
                  disabled={replyGateOverrideIsPending}
                >
                  {replyGateOverrideIsPending
                    ? 'Creating draft…'
                    : REPLY_GATE_OVERRIDE_ACTION_LABEL}
                </Button>
              </DialogFooter>
            </DialogContent>
          )}
        </Dialog>

        <Dialog open={rejectConfirmationOpen} onOpenChange={setRejectConfirmationOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject this draft?</DialogTitle>
              <DialogDescription>
                The draft will be removed from the pending queue. The conversation is not
                deleted.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                className="min-h-11 md:min-h-11"
                onClick={() => setRejectConfirmationOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                className="min-h-11 md:min-h-11"
                onClick={confirmReject}
                disabled={rejectMutation.isPending}
                data-testid="confirm-reject"
              >
                {rejectMutation.isPending ? 'Rejecting…' : 'Reject draft'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={discardConfirmationOpen}
          onOpenChange={(open) => {
            if (open) {
              setDiscardConfirmationOpen(true);
            } else {
              closeDiscardConfirmation();
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Discard unsaved changes?</DialogTitle>
              <DialogDescription>
                Your edited draft has not been sent. Keep editing to preserve it, or discard
                the changes and continue.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                className="min-h-11 md:min-h-11"
                onClick={closeDiscardConfirmation}
              >
                Keep editing
              </Button>
              <Button
                type="button"
                variant="destructive"
                className="min-h-11 md:min-h-11"
                onClick={confirmDiscardChanges}
              >
                Discard changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={deleteConfirmationOpen}
          onOpenChange={(open) => {
            if (!open && softDeleteMutation.isPending) return;
            if (!open) {
              closeDeleteConfirmation();
            } else {
              setDeleteConfirmationOpen(true);
            }
          }}
        >
          <DialogContent
            onKeyDownCapture={(event) => {
              if (event.key !== 'Escape') return;
              event.preventDefault();
              event.stopPropagation();
              closeDeleteConfirmation();
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              window.requestAnimationFrame(() => deleteTriggerRef.current?.focus());
            }}
          >
            <DialogHeader>
              <DialogTitle>Move thread to trash?</DialogTitle>
              <DialogDescription>
                {isDraftDirty &&
                selectedThread &&
                pendingDeleteThreadIds.includes(selectedThread.thread_id)
                  ? 'This will discard your unsaved draft changes and move the thread to Trash. This action requires your explicit confirmation.'
                  : pendingDeleteThreadIds.length > 1
                    ? `This will move ${pendingDeleteThreadIds.length} threads to Trash.`
                    : 'This will move the thread to Trash.'}
              </DialogDescription>
            </DialogHeader>
            {deleteError && (
              <p role="alert" className="text-sm text-destructive">
                {deleteError}
              </p>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                className="min-h-11 md:min-h-11"
                onClick={closeDeleteConfirmation}
                disabled={softDeleteMutation.isPending}
              >
                {isDraftDirty &&
                selectedThread &&
                pendingDeleteThreadIds.includes(selectedThread.thread_id)
                  ? 'Keep editing'
                  : 'Cancel'}
              </Button>
              <Button
                type="button"
                variant="destructive"
                className="min-h-11 md:min-h-11"
                onClick={confirmDeleteThreads}
                disabled={softDeleteMutation.isPending}
              >
                {softDeleteMutation.isPending
                  ? 'Moving…'
                  : isDraftDirty &&
                      selectedThread &&
                      pendingDeleteThreadIds.includes(selectedThread.thread_id)
                    ? 'Discard changes & delete'
                    : 'Move to trash'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showShortcutsHelp} onOpenChange={setShowShortcutsHelp}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Keyboard Shortcuts</DialogTitle>
              <DialogDescription className="sr-only">
                Reference for keyboard shortcuts available in the Inbox.
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {shortcuts.map((shortcut) => (
                <div key={shortcut.key} className="flex items-center justify-between gap-2">
                  <kbd className="rounded bg-muted px-2 py-1 text-xs font-mono">
                    {shortcut.key.toUpperCase()}
                  </kbd>
                  <span className="text-muted-foreground">{shortcut.description}</span>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
    </MotionConfig>
  );
}
